import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, AfterViewInit, OnDestroy, ViewChild, QueryList, ViewContainerRef, ChangeDetectorRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatMenuModule } from '@angular/material/menu';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ConfigService } from '../../shared-services/config.service';
import { Observable, Subject, forkJoin, map, switchMap, takeUntil } from 'rxjs';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { SimulationService } from '../../shared-services/simulation.service';
import { formatDisplayValueByContext, formatValueInMillions, formatWholeNumberWithCommas } from '../../utils/format.utils';
import { CustomPayloadService } from '../../../../../my-app/src/app/shared/dashboard-payload-services/custom-payload.service';
import { PriceFileSimulationService } from '../../shared-services/price-file-simulation.service';
import { SaveSimulationDialogComponent } from '../../global-components/save-simulation-dialog/save-simulation-dialog.component';
import { FilterDialogComponent } from '../../global-components/filter-dialog/filter-dialog.component';

type SimulationDropdownRow = {
  property: string;
  value: string;
  allowManualEntry: boolean;
  value_options?: string[];
};

@Component({
  selector: 'app-editable-table',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatMenuModule,
    MatCheckboxModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatSelectModule
  ],
  templateUrl: './editable-table.component.html',
  styleUrl: './editable-table.component.scss'
})
export class EditableTableComponent implements OnInit, OnDestroy {
  private static globalRunFreezeCount = 0;
  private static globalRunFreezeOverlay: HTMLDivElement | null = null;
  private readonly APPLIED_FILTER_DEBUG_KEY = '[APPLIED_FILTER_DEBUG]';
  private readonly RUN_SAVE_APPLIED_FILTER_DEBUG_KEY = '[RUN_SAVE_APPLIED_FILTER_DEBUG]';
  @Input() data!: any;
  years: any = [];

  @Input() set widgetId(val: string) {
    this._widgetId = val;
  }
  get widgetId(): string {
    return this._widgetId || this.data?.widgetId;
  }

  private _widgetId!: string;
  form: FormGroup;
  isLoading = false;
  isRunWindowFrozen = false;
  lastAction: string | null = null; // Track last action
  successMessage: string | null = null;
  errorMessage: string | null = null;
  // isValidationSuccessful = false;
  areActionsDisabled = false;
  baseYear = 2025;
  yearRange: number[] = [2026, 2027, 2028, 2029, 2030];
  inputsVisible = false;
  editedPercentage: { [barKey: string]: number } = {};
  editedNotes: { [noteKey: string]: string } = {};
  private unSubscribe$ = new Subject<void>();
  public formatDecimalWithCommas = formatWholeNumberWithCommas;
  public formatValueInMillions = formatValueInMillions;
  private currentFilters: any = {};
  private lastEditedYear: string | null = null;
  private originalPercentages: { [key: string]: number } = {};
  private editedYears = new Set<string>(); // keeps track of edited years in opportunity  
  readonly priceChangeValueInfoMessage = 'Please enter a valid number (e.g., 6 or 6.7)';

  // Generic table mode properties
  isGenericTableMode = false;
  genericTableData: any[] = [];
  genericColumns: { key: string; label: string; isFilterable?: boolean; width?: number; type?: string; options?: any[]; showTotal?: boolean; isEditable?: boolean }[] = [];
  genericLoading = true;

  filterValues: { [key: string]: string } = {};
  selectedRows = new Set<any>();
  headerTotals: { [key: string]: string } = {};

  // Excel-style filter properties
  uniqueColumnValues: { [key: string]: any[] } = {};
  selectedFilterValues: { [key: string]: Set<any> } = {};
  filterSearchText: { [key: string]: string } = {};
  dropdownSearchText: { [key: string]: string } = {};
  filteredData: any[] = []; // Displayed data after filtering
  originalGenericData: any[] = []; // Backup of full data
  appliedFilters: string[] = []; // Store applied filters as strings for display
  structuredAppliedFilters: any[] = []; // Store applied filters as objects for API
  appliedFilterDialogState: any = null; // Preserve exact AND/OR + nested rule-set UI state
  appliedFiltersEditMode = false;
  private simulationFilterValueRow: any | null = null;
  private simulationPriceChangeValueRow: any | null = null;
  private readonly simulationFiltersFieldLabelMap: Record<string, string> = {
    name: 'Name',
    code: 'Code',
    region: 'Region',
    part_number: 'Part Number',
    pricing_admin_owner: 'Pricing Admin Owner',
    product_bu: 'Product BU',
    business_owner: 'Business Owner',
    part_owner: 'Part Owner',
    unique_common: 'Unique Common',
    regional_pricing_flag: 'Regional Pricing Flag',
    paccar_part: 'PACCAR Part',
    avg_resulting_cost: 'Avg Resulting Cost',
    resulting_cost: 'Resulting Cost',
    price_below_cost: 'Price Below Cost',
    avg_current_price: 'Avg Current Price',
    current_price: 'Current Price',
    current_effective_date: 'Current Effective Date',
    end_date: 'End Date',
    associated_fixed_price: 'Associated Fixed Price',
    publish_price: 'Publish Price',
    publish_effective_date: 'Publish Effective Date',
    calculated_price: 'Calculated Price',
    cpm_initial_price: 'CPM Initial Price',
    future_price: 'Future Price',
    percent_change: 'Percent Change',
    future_effective_date: 'Future Effective Date',
    pricing_action_history: 'Pricing Action History',
    core_xref_report: 'Core xRef Report',
    annual_volume: 'Annual Volume',
    forecast_annual_volume: 'Forecast Annual Volume',
    forecast_multiplier: 'Forecast Multiplier',
    pricing_action: 'Pricing Action',
    select_pricing_action: ' Select Pricing Action',
    segment_change_reason: 'Segment Change Reason',
    external_communication_change_reason: 'External Communication Change Reason',
    change_reason: 'Change Reason',
    notes: 'Notes',
    publish_flag: 'Publish Flag',
    brand: 'Brand',
    brand_minor: 'Brand Minor',
    wpt: 'WPT',
    engine_model: 'Engine Model',
    certification_level: 'Certification level',
    displacement_liters: 'Displacement Liters',
    jv_identifier: 'JV Identifier',
    cpm: 'CPM',
    cpm_lead: 'CPM Lead',
    apm_code: 'APM Code',
    segment_type: 'Segment Type',
    segment: 'Segment',
    ecc: 'ECC',
    current_margin_pct: 'Current Margin Pct',
    saleable: 'Saleable',
    sales_status_code: 'Sales Status Code',
    future_sales_status_code: 'Future Sales Status Code',
    cpm_lead_approval: 'CPM Lead Approval',
    cpm_lead_approval_note: 'CPM Lead Approval Note',
    dir_of_pm1_approval: 'Dir of PM1 Approval',
    dir_of_pm2_approval: 'Dir of PM2 Approval',
    exec_director_approval: 'Exec Director Approval',
    pricing_mgr_approval: 'Pricing Mgr Approval',
    core_mgr_approval: 'Core Mgr Approval',
    approval_reason: 'Approval Reason',
    global_impact: 'Global Impact',
    global_total_impact: 'Global Total Impact',
    regional_impact: 'Regional Impact',
    swapp: 'SWAPP',
    swapp_program_analysis_code: 'SWAPP Program Analysis Code',
    previous_part_number: 'Previous Part Number',
    previous_part_price: 'Previous Part Price',
    previous_part_resulting_cost: 'Previous Part Resulting Cost',
    component_cost_aggregate: 'Component Cost Aggregate',
    d_core_value: 'D Core Value',
    outright_price: 'Outright Price',
    information: 'Information',
    effective_pvc_code: 'Effective PVC Code',
    price_discrepancy: 'Price Discrepancy',
    restricted_pricing: 'Restricted Pricing',
    fin_responsibility_code: 'Fin Responsibility Code',
    is_dnp_addition: 'Is DNP Addition',
    ebu_ctt_compare: 'EBU CTT Compare',
    lifecycle: 'Lifecycle',
    sales_velocity: 'Sales Velocity',
    accenture_part: 'Accenture Part',
    resulting_cost_history: 'Resulting Cost History',
    regional_low_volume: 'Regional Low Volume',
    stocking_segment: 'Stocking Segment',
    dtna_part_flag: 'DTNA Part Flag',
    dtna_sod_part_flag: 'DTNA SOD Part Flag',
    engine_type: 'Engine Type',
    ecc_minor: 'ECC Minor',
    id: 'id',
    updated_by: 'Updated By',
    updated_date: 'Last Updated On',
    validationstatus: 'Validation Status',
    is_valid: 'Is Valid',
    created_date: 'Created On',
    created_by: 'Created By',
    comment: 'Annotation',
    base_file: 'Base File',
    channel: 'Channel',
    currency: 'Currency',

    oem_published_to_goms_date: 'OEM Published To GOMS Date',
    published_to_goms_date: 'Published To GOMS Date',
    current_oem_calculated_price: 'Current OEM Calculated Price',
    oem_calculated_price: 'OEM Calculated Price',
    oem_future_price: 'OEM Future Price',
    oem_future_effective_date: 'OEM Future Effective Date',

    oem_pricing_action: 'OEM Pricing Action',
    part_oem_active_flag: 'Part OEM Active Flag',
    part_oem_special_pricing: 'Part OEM Special Pricing',

    oracle_check_date: 'Oracle Check Date',
    oracle_file: 'Oracle File',
    outbound_staged_date: 'Outbound Staged Date',
    dn_pricing_action: 'DN Pricing Action',
    resend_current_price: 'Resend Current Price',
    resend_publish_price: 'Resend Publish Price',
    trade_discount_code: 'Trade Discount Code',
    current_part_number: 'Current Part Number',
    last_published_dn_pricing_action: 'Last Published DN Pricing Action',
    future_end_date: 'Future End Date',
    oem_future_end_date: 'OEM Future End Date',
    published_to_goms_end_date: 'Published To GOMS End Date',
    oem_published_to_goms_end_date: 'OEM Published To GOMS End Date',
    short_part_number: 'Short Part Number',
    unit_of_measure: 'Unit Of Measure',
    publish_to_sap: 'Publish To SAP',
    sap_staged_date: 'SAP Staged Date',
    resend_sap_flag: 'Resend SAP Flag'
  };

  private normalizeManualFilterValues(rawValue: any, allowWhitespaceSplit = false): string[] {
    const normalizeArray = (values: any[]): string[] => values
      .map((value: any) => String(value ?? '').trim().replace(/^['"]|['"]$/g, ''))
      .filter((value: string) => value !== '');

    if (Array.isArray(rawValue)) {
      return normalizeArray(rawValue);
    }

    const raw = String(rawValue ?? '').trim();
    if (!raw) {
      return [];
    }

    if (raw.startsWith('[') && raw.endsWith(']')) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return normalizeArray(parsed);
        }
      } catch {
        // Fall through to legacy list parser (e.g. [a, b]).
      }

      const legacyInner = raw.slice(1, -1).trim();
      if (!legacyInner) {
        return [];
      }
      return legacyInner
        .split(',')
        .map((value: string) => value.trim().replace(/^['"]|['"]$/g, ''))
        .filter((value: string) => value !== '');
    }

    if (allowWhitespaceSplit && raw.includes(',')) {
      return raw
        .split(',')
        .map((value: string) => value.trim())
        .filter((value: string) => value !== '');
    }

    if (allowWhitespaceSplit) {
      const tokens = raw
        .split(/\s+/)
        .map((value: string) => value.trim())
        .filter((value: string) => value !== '');

      return tokens.length > 1 ? tokens : [raw];
    }

    return [raw];
  }

  private toPayloadFilterValue(value: any, forceArray = false, operator?: string): string | string[] {
    const op = String(operator || '').trim().toLowerCase();
    if (op === 'is_null' || op === 'is_not_null' || op === 'is null' || op === 'is not null') {
      return 'null';
    }
    const normalizedValues = this.normalizeManualFilterValues(value, false);
    if (normalizedValues.length === 0) {
      return forceArray ? [] : '';
    }
    if (!forceArray && normalizedValues.length === 1) {
      return normalizedValues[0];
    }
    return normalizedValues;
  }

  private normalizeFieldToken(value: any): string {
    return String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  private getFieldMatchTokens(field: any): string[] {
    const raw = String(field ?? '').trim();
    if (!raw) {
      return [];
    }

    const tokens = new Set<string>();
    const addToken = (value: any) => {
      const normalized = this.normalizeFieldToken(value);
      if (normalized) {
        tokens.add(normalized);
      }
    };

    addToken(raw);

    // Handle API values like "code - Label".
    const dashedParts = raw.split(' - ');
    if (dashedParts.length > 1) {
      addToken(dashedParts[0]);
      addToken(dashedParts.slice(1).join(' - '));
    }

    // Handle API values like "code{Label}".
    const braceMatch = raw.match(/^([^{]+)\{([^}]+)\}$/);
    if (braceMatch) {
      addToken(braceMatch[1]);
      addToken(braceMatch[2]);
    }

    // Handle values with numeric prefixes like "33 - Region".
    addToken(raw.replace(/^\d+\s*[-:]\s*/, ''));

    return Array.from(tokens);
  }

  private shouldUseDropdownFilterValueEntry(field: string): boolean {
    const dropdownFields = Array.isArray(this.data?.dropdownValueFields)
      ? this.data.dropdownValueFields
      : [];
    if (!dropdownFields.length) {
      return true;
    }

    const configuredTokens = new Set(
      dropdownFields
        .map((item: any) => this.normalizeFieldToken(item))
        .filter((token: string) => token !== '')
    );
    const fieldTokens = this.getFieldMatchTokens(field);
    return fieldTokens.some((token) => configuredTokens.has(token));
  }

  private updateFilterValueEntryMode(field: string): void {
    if (!this.simulationFilterValueRow) {
      return;
    }

    const useDropdownEntry = this.shouldUseDropdownFilterValueEntry(field);
    this.simulationFilterValueRow.allowManualEntry = !useDropdownEntry;
    this.simulationFilterValueRow['value_options'] = [];
    this.simulationFilterValueRow.value = '';

    if (!field || !useDropdownEntry) {
      return;
    }

    this.loadFilterValueOptions(field);
  }

  constructor(
    private fb: FormBuilder,
    private configService: CustomPayloadService,
    private globalFilterService: GlobalFilterService,
    private apiConfigService: ConfigService,
    private snackBar: MatSnackBar,
    private router: Router,
    private simulationService: SimulationService,
    private dialog: MatDialog,
    private priceFileSimulationService: PriceFileSimulationService,
    private route: ActivatedRoute,
    private location: Location,
    private cdr: ChangeDetectorRef
  ) {
    this.form = this.fb.group({});
  }

  private logRunSaveAppliedFilters(stage: string, data: any): void {
    try {
      console.log(this.RUN_SAVE_APPLIED_FILTER_DEBUG_KEY, stage, JSON.parse(JSON.stringify(data)));
    } catch {
      console.log(this.RUN_SAVE_APPLIED_FILTER_DEBUG_KEY, stage, data);
    }
  }

  ngOnInit(): void {
    // Listen for simulation results if this is the Summary widget
    if (this.widgetId === 'simulation-summary') {
      this.simulationService.simulationResults$.pipe(takeUntil(this.unSubscribe$)).subscribe(results => {
        if (results) {
          this.updateSummaryTable(results);
        }
      });
    }

    // Listen for global actions (e.g. from footer buttons)
    this.globalFilterService.filter$.pipe(takeUntil(this.unSubscribe$)).subscribe(filters => {
      if (filters?.requestParams?.action?.[0] === 'save-simulation') {
        // Only trigger if this is the main simulation list OR if we are on the 'New' page
        // Actually, on 'New' page, multiple widgets might exist. We want only one to handle it.
        // Usually the 'simulation-table' or a designated primary widget.
        if (this.widgetId === 'simulation-table' || this.widgetId === 'simulation-values') {
          this.handleSaveSimulation();
          // Clear action after handling to avoid double triggers if multiple filters emitted
          this.globalFilterService.updateFilterSelection('action', [], true);
        }
      }
    });

    // Check if this is a generic table mode (headerColumns present)
    if (this.data?.headerColumns?.length > 0) {
      this.isGenericTableMode = true;
      this.genericColumns = this.data.headerColumns;
      this.initGenericEmptyTable();
      if (this.widgetId === 'simulation-filters') {
        this.loadSimulationFiltersTable();
        return;
      }
      if (this.widgetId === 'simulation-values') {
        this.loadSimulationValuesTable();
        return;
      }
      this.loadGenericTableData();
      return;
    }

    this.initForm();
    this.globalFilterService.filter$
      .pipe(takeUntil(this.unSubscribe$),
        switchMap((filters) => {
          this.currentFilters = filters;
          const isSelfUpdate = filters?.widgetId === this.widgetId;
          const isSliderFilter = filters?.queryParams?.slider_range;
          if (isSelfUpdate && !isSliderFilter) {
            return;
          }
          return this.fetchData(filters);

        })
      )

      .subscribe({
        next: (res: any) => {
          this.updateFormWithApiResponse(res);
          this.isLoading = false;
        },
        error: (err) => {
          this.isLoading = false;
          this.errorMessage = 'Failed to fetch data.';
          setTimeout(() => this.clearMessages(), 3000);
        },
      });
  }


  initForm(): void {
    if (this.data?.fields) {
      for (const field of this.data.fields) {
        this.form.addControl(field.key, new FormControl(field.default || ''));
      }
    }
    // Initialize applied filters from data if present
    if (this.data?.appliedFilters) {
      this.appliedFilters = [...this.data.appliedFilters];
    }
  }

  fetchData(filters: any): any {
    this.isLoading = true;
    this.clearMessages();
    const sliderRange = filters?.queryParams?.['slider_range'];
    if (Array.isArray(sliderRange) && sliderRange.length === 2) {
      const startOffset = sliderRange[0];
      const endOffset = sliderRange[1];
      this.generateYearRange(startOffset, endOffset);
      this.inputsVisible = true;
      if (endOffset === 1) {
        this.areActionsDisabled = true;
      } else {
        this.areActionsDisabled = false;
      }
    } else {
      this.inputsVisible = false;
      this.areActionsDisabled = false;
    }

    // Strip widgetId from queryParams but keep the rest
    const { widgetId, ...queryParamsWithoutWidget } = filters?.queryParams || {};
    const finalPayload = {
      ...filters,
      queryParams: queryParamsWithoutWidget,
      useQueryParams: this.data?.useQueryParams || false,
    };
    return this.configService.getConfig(this.data.submitApiUrl, finalPayload)
      .pipe(takeUntil(this.unSubscribe$))
  }

  private updateFormWithApiResponse(res: any) {
    const percentage = res?.totalOpportunity?.percentage || [];
    const priceList = res?.totalOpportunity?.priceList || [];
    const notesList = res?.totalOpportunity?.notesList || [];
    const editable = res?.totalOpportunity?.editable || [];
    this.years = res?.totalOpportunity?.years;
    this.yearRange.forEach((year, index) => {
      const opportunityKey = `opportunity_${year}`;
      const salesKey = `sales_${year}`;
      const notesKey = `notes_${year}`;

      if (!this.form.contains(opportunityKey)) {
        this.form.addControl(opportunityKey, new FormControl(''));
      }
      if (!this.form.contains(salesKey)) {
        this.form.addControl(salesKey, new FormControl({ value: '', disabled: true }));
      }
      if (!this.form.contains(notesKey)) {
        this.form.addControl(notesKey, new FormControl(''));
      }
      const rawValue = percentage[year - this.baseYear];

      if (rawValue !== null && rawValue !== undefined && !isNaN(rawValue)) {
        this.originalPercentages[opportunityKey] = rawValue;
      }

      const formattedValue =
        rawValue !== null && rawValue !== undefined && !isNaN(rawValue)
          ? parseFloat(parseFloat(rawValue).toFixed(1)) // parsed years  opportunity input to  number, not string
          : null;

      const editedKey = String(year - this.baseYear);

      // set the newly edited year value in input 
      if (!this.editedYears.has(editedKey)) {
        this.form.get(opportunityKey)?.setValue(formattedValue, { emitEvent: false });
      }
      this.form.get(salesKey)?.setValue(priceList[year - this.baseYear] ?? '');
      this.form.get(notesKey)?.setValue(notesList[year - this.baseYear] ?? '');

      const isEditable = editable[year - this.baseYear] !== false;

      this.form.get(notesKey)?.enable();
      if (isEditable) {
        this.form.get(opportunityKey)?.enable();
      } else {
        this.form.get(opportunityKey)?.disable();
      }
    });
  }

  getControl(name: string): FormControl {
    return this.form.get(name) as FormControl;
  }

  generateYearRange(startOffset: number, endOffset: number) {
    this.yearRange = [];

    for (let i = startOffset + 1; i <= endOffset; i++) {
      const year = this.baseYear + i;
      this.yearRange.push(year);
    }
  }

  onOpportunityChange(year: number): void {
    // this.isValidationSuccessful = false;

    const opportunityControlName = `opportunity_${year}`;
    const opportunityValue = this.form.get(opportunityControlName)?.value;

    // console.log(`[onOpportunityChange] Opportunity Value for ${year}:`, opportunityValue);

    if (opportunityValue === '' || opportunityValue === null || isNaN(opportunityValue)) {
      // console.warn(`[onOpportunityChange] Invalid opportunity value, skipping API call.`);
      return;
    }

    // Add just the changed opportunity%
    const barKey = `bar${year - this.baseYear}`;
    const editedKey = `${year - this.baseYear}`;
    this.editedYears.add(editedKey);
    this.lastEditedYear = editedKey;
    this.editedPercentage[barKey] = opportunityValue;

    // Rebuild editedNotes with all non-empty notes from the form
    this.editedNotes = {}; // Clear previous state to avoid stale data
    this.yearRange.forEach((yr) => {
      const noteControl = this.form.get(`notes_${yr}`);
      const noteValue = noteControl?.value;
      const noteKey = `note${yr - this.baseYear}`;
      if (noteValue && noteValue.trim() !== '') {
        this.editedNotes[noteKey] = noteValue;
      }
    });

    const cleanQueryParams = { ...this.currentFilters.queryParams };
    delete cleanQueryParams.barValues;
    delete cleanQueryParams.widgetId;

    const queryParams = {
      ...cleanQueryParams,
      editedYear: editedKey,
      ...this.editedPercentage,
      ...this.editedNotes
    };

    this.configService
      .getConfig(this.data.apiUrl, {
        ...this.currentFilters,
        queryParams,
        useQueryParams: true,
      })
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (res: any) => {
          this.editedYears.clear();
          this.updateFormWithApiResponse(res);
        },
        error: (err) => {
        },
      });
  }


  onOpportunityEnter(year: number, event: Event) {
    this.onOpportunityChange(year);
    (event.target as HTMLElement).blur();
  }

  onEnter(year: number, event: Event) {

    (event.target as HTMLElement).blur();
  }
  handleAction(actionKey: string) {
    switch (actionKey) {
      case 'recalculate':
        this.onRecalculate();
        break;
      case 'submit':
        this.onSubmit();
        break;
      case 'cancel':
        this.onCancel();
        break;
      case 'reset':
        this.onReset();
        break;
      case 'ComparisonView':
      case 'FilteredView':
        this.globalFilterService.updateFilterSelection('viewType', [actionKey], true);
        break;
    }
  }

  onSubmit() {
    this.lastAction = 'submit';
    this.clearMessages();
    this.isLoading = true;
    const newBarValues: any = {};
    this.yearRange.forEach((year) => {
      const controlName = `opportunity_${year}`;
      const value = this.form.get(controlName)?.value;
      if (value !== '' && value !== null && !isNaN(value)) {
        const barKey = `bar${year - this.baseYear}`;
        newBarValues[barKey] = this.originalPercentages[controlName] ?? value;
        //  newBarValues[barKey] = value;  
      }

      const notesControlName = `notes_${year}`;
      const notesValue = this.form.get(notesControlName)?.value;
      if (notesValue !== '' && notesValue !== null) {
        const noteKey = `note${year - this.baseYear}`;
        newBarValues[noteKey] = notesValue;
      }
    });

    const cleanQueryParams = { ...this.currentFilters.queryParams };
    delete cleanQueryParams.barValues;
    delete cleanQueryParams.widgetId;
    const queryParams = { ...cleanQueryParams, ...newBarValues };

    this.configService
      .getConfig(this.data.submitApiUrl, {
        ...this.currentFilters,
        queryParams,
        useQueryParams: true,
      })
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (res: any) => {
          this.editedYears.clear();
          this.updateFormWithApiResponse(res);
          this.globalFilterService.updateFilterSelection('widgetId', [this.widgetId], true);
          this.editedPercentage = {};
          this.isLoading = false;
          this.successMessage = 'Submit successful!';
          // this.isValidationSuccessful = false;
          setTimeout(() => this.clearMessages(), 5000);
        },
        error: (err) => {
          this.isLoading = false;
          this.errorMessage = 'Submit failed. Please try again.';
          setTimeout(() => this.clearMessages(), 5000);
        },
      });
  }

  onCancel() {
    this.lastAction = null;
    this.editedPercentage = {};
    this.lastEditedYear = null;
    // this.isValidationSuccessful = false;
    this.fetchData(this.currentFilters).subscribe({
      next: (res: any) => {
        this.updateFormWithApiResponse(res);
        this.isLoading = false;
      },
      error: (err: any) => {
        this.isLoading = false;
        this.errorMessage = 'Failed to fetch data.';
        setTimeout(() => this.clearMessages(), 3000);
      },
    });

    this.editedYears.clear();

  }

  onReset() {
    this.lastAction = 'reset';
    this.editedPercentage = {};
    this.lastEditedYear = null;
    // this.isValidationSuccessful = false;

    // Clone current filters
    const resetFilters = { ...this.currentFilters };

    //  Add query param: reset=reset
    resetFilters.queryParams = {
      ...(resetFilters.queryParams || {}),
      reset: 'reset'
    };

    //Add full list for each field in sendAllOptionsField
    if (this.data?.sendAllOptionsField?.length) {
      this.data.sendAllOptionsField.forEach((fieldKey: string) => {
        if (this.globalFilterService.dropdownOptions?.[fieldKey]) {
          resetFilters.requestParams = {
            ...(resetFilters.requestParams || {}),
            [fieldKey]: this.globalFilterService.dropdownOptions[fieldKey]
          };
        }
      });
    }

    // added logic to reset the chart values on reset click 
    this.isLoading = true;

    this.configService
      .getConfig(this.data.submitApiUrl, {
        ...resetFilters,
        useQueryParams: true,
      })
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (res: any) => {
          this.updateFormWithApiResponse(res);

          //  refresh chart
          this.globalFilterService.updateFilterSelection(
            'widgetId',
            [this.widgetId],
            true
          );

          this.isLoading = false;
          this.successMessage = 'Reset successful!';
          setTimeout(() => this.clearMessages(), 5000);
        },
        error: (err) => {
          this.isLoading = false;
          this.errorMessage = 'Reset failed. Please try again.';
          setTimeout(() => this.clearMessages(), 5000);
        },
      });
    this.fetchData(resetFilters);
    this.editedYears.clear();
  }




  onRecalculate() {
    this.lastAction = 'recalculate';
    this.clearMessages();
    this.isLoading = true;

    const newBarValues: any = {};

    this.yearRange.forEach((year) => {
      const opportunityControlName = `opportunity_${year}`;
      const opportunityValue = this.form.get(opportunityControlName)?.value;

      if (opportunityValue !== '' && opportunityValue !== null && !isNaN(opportunityValue)) {
        const barKey = `bar${year - this.baseYear}`;
        newBarValues[barKey] = opportunityValue;
      }


      const notesControlName = `notes_${year}`;
      const notesValue = this.form.get(notesControlName)?.value;
      if (notesValue !== '' && notesValue !== null) {
        const noteKey = `note${year - this.baseYear}`;
        newBarValues[noteKey] = notesValue;
      }
    });

    const cleanQueryParams = { ...this.currentFilters.queryParams };
    delete cleanQueryParams.barValues;
    delete cleanQueryParams.widgetId; // Optional: remove if widgetId isn't needed

    const queryParams: any = { ...cleanQueryParams, ...newBarValues };
    if (this.lastEditedYear) {
      queryParams.editedYear = this.lastEditedYear;
    }

    this.configService
      .getConfig(this.data.apiUrl, {
        ...this.currentFilters,
        queryParams,
        useQueryParams: true,
      })
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (res: any) => {
          this.updateFormWithApiResponse(res);
          this.isLoading = false;

          const validationError = res?.totalOpportunity?.validationError;

          if (validationError) {
            this.errorMessage = 'Validation failed. Please try again.';
            // this.isValidationSuccessful = false;
          } else {
            this.successMessage = 'Validation successful!';
            // this.isValidationSuccessful = true;
          }

          setTimeout(() => this.clearMessages(), 5000);
        },
        error: () => {
          this.isLoading = false;
          this.errorMessage = 'Validation failed. Please try again.';
          // this.isValidationSuccessful = false;
          setTimeout(() => this.clearMessages(), 5000);
        },
      });
  }

  clearMessages() {
    this.successMessage = null;
    this.errorMessage = null;
  }

  goBack() {
    // Navigate deterministically to the simulation dashboard
    this.router.navigate(['/price-file-simulation']);
  }

  /**
   * Extract the real DB UUID from a businessId.
   * businessId format: "A0C6889F4A024B13A826EAD093CEAD36-sim_quote-1773080991894"
   * The first segment (32 hex chars) is the UUID without dashes.
   * Returns formatted UUID: "A0C6889F-4A02-4B13-A826-EAD093CEAD36"
   */
  private extractUuidFromBusinessId(businessId: string): string {
    if (!businessId) return '';
    // Split on '-entity-' pattern: take everything before the entity name
    const parts = businessId.split('-');
    // businessId hex part is 32 chars, which splits into segments by dashes
    // Format: XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX (standard UUID = 8-4-4-4-12)
    // But businessId has NO dashes in the hex, so first segment before entity is 32 hex chars
    // Example: A0C6889F4A024B13A826EAD093CEAD36-sim_quote-1773080991894
    // We need to find the hex prefix
    const match = businessId.match(/^([0-9A-Fa-f]{32})/);
    if (match) {
      const hex = match[1];
      // Format as UUID: 8-4-4-4-12
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`.toUpperCase();
    }
    return '';
  }

  handleDirectSave() {
    this.isLoading = true;

    // 1. Determine simulation name from query params or simulationService
    const queryParams = this.route.snapshot.queryParams;
    let name = queryParams['name'] || '';

    // If no name in query params (e.g. on /new page), get from simulationService
    if (!name) {
      const recentSims = this.simulationService.getNewSimulations();
      if (recentSims.length > 0) {
        name = recentSims[recentSims.length - 1].name || '';
      }
    }

    console.log('[SaveSummary] Starting save for name:', name, 'queryParams:', JSON.stringify(queryParams));

    // 2. Extract summary values from the displayed summary table
    const getSummaryValue = (metric: string): string => {
      const row = this.genericTableData?.find((r: any) => r.metric === metric);
      return row?.value ?? '';
    };

    const currentUnitsStr = getSummaryValue('Current Annual Units');
    const totalCurrentAnnualUnits = parseFloat(currentUnitsStr.replace(/,/g, '')) || 0;

    console.log('[SaveSummary] Summary values:', {
      currentSales: getSummaryValue('Current Sales'),
      dollarImpact: getSummaryValue('Simulated Impact (USD)'),
      impactPercent: getSummaryValue('Simulated Impact %'),
      currentUnits: totalCurrentAnnualUnits,
      genericTableDataLength: this.genericTableData?.length
    });

    this.logRunSaveAppliedFilters('EditableTable.handleDirectSave.start', {
      widgetId: this.widgetId,
      routeQueryParams: queryParams,
      selectedUuid: this.getSelectedSimulationUuid(),
      simulationServiceLastRunUuid: this.simulationService.getLastRunUuid(),
      simulationServiceLastRunAppliedFilters: this.simulationService.getLastRunAppliedFilters(),
      simulationServiceCurrentAppliedFilters: this.simulationService.getAppliedFilters(),
      structuredAppliedFilters: this.structuredAppliedFilters,
      displayAppliedFilters: this.appliedFilters
    });

    // 3. Resolve UUID using the same source as Run button first.
    // Run uses selected UUID from route query params; we also persist it in SimulationService.
    let resolvedUuid = this.getSelectedSimulationUuid() || this.simulationService.getLastRunUuid();
    console.log('[SaveSummary] Preferred UUID (run-aligned):', resolvedUuid);

    // Secondary source: extract from businessId query param
    if (!resolvedUuid) {
      const businessId = queryParams['businessId'] || '';
      if (businessId) {
        resolvedUuid = this.extractUuidFromBusinessId(businessId);
        console.log('[SaveSummary] Extracted UUID from businessId:', resolvedUuid, 'businessId:', businessId);
      }
    }

    // Tertiary source: check simulationService for recently saved simulations
    if (!resolvedUuid) {
      const recentSims = this.simulationService.getNewSimulations();
      if (recentSims.length > 0) {
        const lastSim = recentSims[recentSims.length - 1];
        if (lastSim.uuid) {
          resolvedUuid = lastSim.uuid;
          console.log('[SaveSummary] Got UUID from simulationService:', resolvedUuid);
        } else if (lastSim.businessId) {
          resolvedUuid = this.extractUuidFromBusinessId(lastSim.businessId);
          console.log('[SaveSummary] Extracted UUID from simulationService businessId:', resolvedUuid);
        }
      }
    }

    if (resolvedUuid) {
      // UUID resolved, proceed to save directly
      console.log('[SaveSummary] Using resolved UUID:', resolvedUuid);
      this.logRunSaveAppliedFilters('EditableTable.handleDirectSave.resolvedUuid', {
        resolvedUuid,
        selectedUuid: this.getSelectedSimulationUuid(),
        simulationServiceLastRunUuid: this.simulationService.getLastRunUuid(),
        name
      });

      this.callSaveSummaryApi(resolvedUuid, name, getSummaryValue, totalCurrentAnnualUnits);
    } else {
      // Fallback: try backend list (may return empty)
      console.log('[SaveSummary] No UUID found locally, trying backend list for name:', name);
      this.priceFileSimulationService
        .getSimulationList()
        .pipe(takeUntil(this.unSubscribe$))
        .subscribe({
          next: (sims: any[]) => {
            const targetName = String(name || '').trim().toLowerCase();
            const match = sims.find((s: any) => String(s.name || '').trim().toLowerCase() === targetName);
            resolvedUuid = match?.uuid || '';
            console.log('[SaveSummary] Backend list resolution:', { targetName, found: !!match, resolvedUuid });

            if (!resolvedUuid) {
              this.isLoading = false;
              console.error('[SaveSummary] Could not resolve UUID for name:', name);
              this.snackBar.open('Could not find simulation UUID. Please try again.', 'Close', {
                duration: 4000, horizontalPosition: 'right', verticalPosition: 'bottom', panelClass: ['error-snackbar']
              });
              return;
            }
            this.logRunSaveAppliedFilters('EditableTable.handleDirectSave.backendResolvedUuid', {
              resolvedUuid,
              targetName,
              matchedRow: match
            });
            this.callSaveSummaryApi(resolvedUuid, name, getSummaryValue, totalCurrentAnnualUnits);
          },
          error: (err) => {
            console.error('[SaveSummary] Failed to fetch simulation list:', err);
            this.isLoading = false;
            this.snackBar.open('Failed to resolve simulation. Please try again.', 'Close', {
              duration: 4000, horizontalPosition: 'right', verticalPosition: 'bottom', panelClass: ['error-snackbar']
            });
          }
        });
    }
  }

  private callSaveSummaryApi(resolvedUuid: string, name: string, getSummaryValue: (m: string) => string, totalCurrentAnnualUnits: number) {
    const parseNumber = (value: any): number => {
      const cleaned = String(value ?? '').replace(/[^0-9.-]/g, '');
      const parsed = parseFloat(cleaned);
      return Number.isNaN(parsed) ? 0 : parsed;
    };

    const appliedFilters = this.getRunAlignedAppliedFiltersForSave(resolvedUuid);
    this.logRunSaveAppliedFilters('EditableTable.callSaveSummaryApi.appliedFiltersSelected', {
      resolvedUuid,
      name,
      selectedAppliedFiltersForSave: appliedFilters,
      simulationServiceLastRunUuid: this.simulationService.getLastRunUuid(),
      simulationServiceLastRunAppliedFilters: this.simulationService.getLastRunAppliedFilters(),
      simulationServiceCurrentAppliedFilters: this.simulationService.getAppliedFilters(),
      structuredAppliedFilters: this.structuredAppliedFilters,
      displayAppliedFilters: this.appliedFilters
    });

    const simulationValues = this.simulationService.getSimulationValues() || [];
    const getSimulationValue = (property: string): any =>
      simulationValues.find((row: any) => row?.property === property)?.value;

    const rawPriceChangeType = String(getSimulationValue('Price Change Type') || '').toLowerCase().trim();
    const rawPriceChangeValue = getSimulationValue('Price Change Value');
    const rawBasedOn = String(getSimulationValue('Based on') || 'current_price');

    const priceChanges = rawPriceChangeValue !== undefined && rawPriceChangeValue !== null && String(rawPriceChangeValue).trim() !== ''
      ? [
        {
          priceChangeType: rawPriceChangeType || 'value',
          priceChangeValue: String(rawPriceChangeValue),
          basedOn: rawBasedOn.toLowerCase().replace(/ /g, '_')
        }
      ]
      : [];

    const simulatedFutureSales = getSummaryValue('Simulated Future Sales') || getSummaryValue('Estimated Future Sales Value');
    const simulatedImpactUSD = getSummaryValue('Simulated Impact (USD)');
    const simulatedImpactPercent = getSummaryValue('Simulated Impact %');
    const totalImpactUsd = getSummaryValue('Total Impact (USD)');
    const totalImpactPercent = getSummaryValue('Total Impact %');

    const summary = {
      totalDollarImpact: simulatedImpactUSD,
      totalImpactResult: simulatedImpactPercent,
      totalCurrentAnnualUnits,
      totalParts: parseNumber(getSummaryValue('Total # Records')),
      totalCurrentTotalMargin: getSummaryValue('Current Margin'),
      totalCurrentFutureSales: getSummaryValue('Current Future Sales'),
      totalCurrentImpact: getSummaryValue('Current Impact (USD)'),
      totalCurrentImpactPercent: getSummaryValue('Current Impact %'),
      totalFutureSalesValue: parseNumber(simulatedFutureSales),
      totalCurrentSalesValue: getSummaryValue('Current Sales'),
      totalOverallImpact: getSummaryValue('Overall Impact %') || simulatedImpactPercent,
      totalPartsImpacted: parseNumber(getSummaryValue('# Parts Impacted')),
      simulatedFutureSales,
      simulatedImpactUSD,
      simulatedImpactPercent,
      // Summary section: Total Impact (USD) / Total Impact %
      totalImpactUsd,
      totalOverallImpactPercent: totalImpactPercent,
      totalImpactPercent
    };

    const summaryPayload = {
      uuid: resolvedUuid,
      source_entity: this.simulationService.getEntity(),
      totalCurrentSalesValue: summary.totalCurrentSalesValue,
      totalDollarImpact: summary.totalDollarImpact,
      totalImpactResult: summary.totalImpactResult,
      totalCurrentAnnualUnits,
      simulatedFutureSales,
      simulatedImpactUSD,
      simulatedImpactPercent,
      appliedFilters,
      priceChanges,
      summary
    };

    this.logRunSaveAppliedFilters('EditableTable.callSaveSummaryApi.summaryPayloadBeforeService', {
      summaryPayload
    });

    console.log('[SaveSummary] Calling API with payload:', JSON.stringify(summaryPayload, null, 2));

    this.priceFileSimulationService
      .saveSummary(summaryPayload)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          this.persistCurrentServiceAppliedFiltersForUuid(resolvedUuid);
          console.log('[SaveSummary] API response commitStatus:', response?.commitStatus);

          const simQuote = response?.responseList?.[0]?.sim_quote?.[0]?.data || {};
          console.log('[SaveSummary] Backend simQuote data:', JSON.stringify(simQuote, null, 2));
          const updatedFields: any = {
            last_execution_date: this.formatDateOnly(simQuote.last_execution_date || new Date().toISOString()),
            current_sales: simQuote.current_sales || getSummaryValue('Current Sales'),
            current_units: simQuote.current_annual_units || simQuote.current_units || getSummaryValue('Current Annual Units'),
            impact_dollar: simQuote.impact_dollar || getSummaryValue('Simulated Impact (USD)'),
            impact_percent: simQuote.impact_percentage || simQuote.impact_percent || getSummaryValue('Simulated Impact %'),
            uuid: resolvedUuid
          };
          console.log('[SaveSummary] Updated fields:', JSON.stringify(updatedFields, null, 2));
          this.simulationService.updateSimulationByName(name, updatedFields);

          // Update the table data directly if this is the simulation table
          console.log('[SaveSummary] Checking widgetId:', this.widgetId, '=== simulation-table?', this.widgetId === 'simulation-table');
          if (this.widgetId === 'simulation-table') {
            const dataIndex = this.genericTableData.findIndex(item => item.name === name);
            console.log('[SaveSummary] Table update - dataIndex:', dataIndex, 'name:', name);
            console.log('[SaveSummary] Table update - genericTableData before:', this.genericTableData[dataIndex]);
            if (dataIndex >= 0) {
              // Update the existing row with new values
              this.genericTableData[dataIndex] = {
                ...this.genericTableData[dataIndex],
                ...updatedFields
              };
              console.log('[SaveSummary] Table update - genericTableData after:', this.genericTableData[dataIndex]);
              // Trigger change detection
              this.cdr.detectChanges();
            }
          } else {
            // If not in simulation table, we need to update the simulation service data
            // so when the table loads, it will have the updated values
            const sims = this.simulationService.getNewSimulations();
            const simIndex = sims.findIndex(s => s.name === name);
            if (simIndex >= 0) {
              sims[simIndex] = { ...sims[simIndex], ...updatedFields };
              // Force update the BehaviorSubject
              this.simulationService['newSimulationsSubject'].next([...sims]);
            }
          }

          this.snackBar.open(`Simulation "${name}" saved successfully!`, 'Close', {
            duration: 3000, horizontalPosition: 'right', verticalPosition: 'bottom', panelClass: ['success-snackbar']
          });
        },
        error: (err) => {
          console.error('[SaveSummary] API call failed:', err);
          this.isLoading = false;
          this.snackBar.open('Failed to save simulation. Please try again.', 'Close', {
            duration: 4000, horizontalPosition: 'right', verticalPosition: 'bottom', panelClass: ['error-snackbar']
          });
        }
      });
  }

  private getRunAlignedAppliedFiltersForSave(resolvedUuid = ''): any[] {
    const lastRunFilters = this.simulationService.getLastRunAppliedFilters();
    const lastRunUuid = String(this.simulationService.getLastRunUuid() || '').trim();
    const saveUuid = String(resolvedUuid || '').trim();
    const currentFilters = Array.isArray(this.simulationService.getAppliedFilters())
      ? this.simulationService.getAppliedFilters()
      : [];
    const useLastRunFilters = lastRunFilters.length > 0 && (!lastRunUuid || !saveUuid || lastRunUuid === saveUuid);
    if (useLastRunFilters) {
      this.logRunSaveAppliedFilters('EditableTable.getRunAlignedAppliedFiltersForSave.usingLastRunFilters', {
        resolvedUuid,
        saveUuid,
        lastRunUuid,
        lastRunFilters,
        currentFilters
      });
      return lastRunFilters;
    }

    const mappedCurrentFilters = currentFilters
      .map((filter: any) => {
        const field = String(filter?.attributeName || filter?.field || '').trim();
        const operator = filter?.operator || '=';
        const forceArray = filter?.multiValueInput === true || this.isListOperator(operator);
        const rawLogical = String(filter?.logicalOperator || '').trim().toUpperCase();
        const logicalOperator = rawLogical === 'OR' || rawLogical === 'AND' ? rawLogical : '';

        return {
          field,
          operator,
          value: this.toPayloadFilterValue(
            Array.isArray(filter?.attributeValues) ? filter.attributeValues : filter?.value,
            forceArray,
            operator
          ),
          logicalOperator,
          beginScope: String(filter?.beginScope || ''),
          endScope: String(filter?.endScope || '')
        };
      })
      .filter((filter: any) => filter.field !== '')
      .map((filter: any, index: number, all: any[]) => ({
        ...filter,
        logicalOperator: index === all.length - 1
          ? ''
          : (filter.logicalOperator === 'OR' ? 'OR' : 'AND')
      }));

    this.logRunSaveAppliedFilters('EditableTable.getRunAlignedAppliedFiltersForSave.usingCurrentFiltersFallback', {
      resolvedUuid,
      saveUuid,
      lastRunUuid,
      lastRunFilters,
      currentFilters,
      mappedCurrentFilters
    });

    return mappedCurrentFilters;
  }

  // --- Generic Table Mode Methods ---

  private initGenericEmptyTable(): void {
    this.genericTableData = [];
    this.genericLoading = false;
  }

  private getSelectedSimulationUuid(): string {
    const queryParams = this.route.snapshot.queryParams || {};
    return String(queryParams['quote_uuid'] || queryParams['uuid'] || queryParams['simulation_uuid'] || '').trim();
  }

  private loadSelectedSimulationDetails(): void {
    const selectedUuid = this.getSelectedSimulationUuid();
    if (!selectedUuid || this.widgetId === 'simulation-table') {
      return;
    }

    this.priceFileSimulationService
      .getSimulationDetails(selectedUuid)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (response) => this.applySimulationDetails(response),
        error: (err) => {
          console.error('Failed to load simulation details', err);
        }
      });
  }

  private applySimulationDetails(response: any): void {
    const detailsData = response?.responseList?.[0]?.simulation?.[0]?.data;
    if (!detailsData) {
      return;
    }

    const summaryData = this.parseSerializedObject(detailsData.summary);
    const appliedFiltersRaw =
      detailsData?.appliedFilters
      ?? detailsData?.applied_filters
      ?? response?.appliedFilters
      ?? response?.applied_filters
      ?? response?.requestData?.appliedFilters
      ?? response?.requestData?.applied_filters;
    console.log(this.APPLIED_FILTER_DEBUG_KEY, 'applySimulationDetails.raw', {
      widgetId: this.widgetId,
      uuid: this.getSelectedSimulationUuid(),
      appliedFiltersRaw
    });
    const appliedFilterData = this.parseSerializedList(appliedFiltersRaw);
    console.log(this.APPLIED_FILTER_DEBUG_KEY, 'applySimulationDetails.parsedList', appliedFilterData);
    const priceChangeData = this.parseSerializedList(detailsData.priceChanges);
    const parsedStructuredAppliedFilters = appliedFilterData
      .map((filter, index) => {
        const operator = filter['operator'] || '=';
        const normalizedOperatorType = String(this.mapOperatorTypeFromSymbol(operator) || '')
          .trim()
          .toUpperCase()
          .replace(/\s+/g, '_');
        const normalizedValues = this.normalizeManualFilterValues(filter['value'] ?? filter['attributeValues'] ?? '', false);
        const normalizedValue = normalizedValues.length <= 1 ? (normalizedValues[0] ?? '') : normalizedValues;
        const forceArray = this.isListOperator(operator);
        const payloadKey = filter['field'] || filter['attributeName'] || '';

        return {
          _operatorType: normalizedOperatorType,
          attributeName: payloadKey,
          field: payloadKey,
          operator,
          attributeValues: this.toPayloadFilterValue(normalizedValue, forceArray, operator),
          value: this.toPayloadFilterValue(normalizedValue, forceArray, operator),
          multiValueInput: forceArray,
          logicalOperator: this.resolveLogicalOperator(filter?.['logicalOperator'], index, appliedFilterData.length),
          beginScope: String(filter?.['beginScope'] || ''),
          endScope: String(filter?.['endScope'] || '')
        };
      })
      .filter((filter) =>
        filter.attributeName !== '' && (
          filter._operatorType === 'IS_NULL'
          || filter._operatorType === 'IS_NOT_NULL'
          || this.hasMeaningfulFilterValue(filter.attributeValues)
        )
      )
      .map(({ _operatorType, ...filter }) => filter as any);
    console.log(this.APPLIED_FILTER_DEBUG_KEY, 'applySimulationDetails.parsedStructured', parsedStructuredAppliedFilters);

    // For GetSimulationDetails response, always honor backend-appliedFilters when provided.
    // Fallback to existing shared state only when backend response has none.
    const existingSharedFilters = Array.isArray(this.simulationService.getAppliedFilters())
      ? this.simulationService.getAppliedFilters()
      : [];
    const structuredAppliedFilters = parsedStructuredAppliedFilters.length > 0
      ? parsedStructuredAppliedFilters
      : existingSharedFilters;
    console.log(this.APPLIED_FILTER_DEBUG_KEY, 'applySimulationDetails.selectedStructured', {
      fromParsed: parsedStructuredAppliedFilters.length,
      fromShared: existingSharedFilters.length,
      selected: structuredAppliedFilters.length
    });

    // Keep shared service in sync for deep-dive request construction across widgets.
    if (structuredAppliedFilters.length > 0 || existingSharedFilters.length > 0) {
      this.simulationService.setAppliedFilters(structuredAppliedFilters);
    }

    // If this widget displays the Applied Filters layout, keep it in sync
    // regardless of whether it is simulation-filters / simulation-values / simulation-summary.
    if (this.data?.showAppliedFilters === true) {
      this.structuredAppliedFilters = [...structuredAppliedFilters];
      this.syncAppliedFiltersDisplayFromStructured();
      if (this.appliedFilters.length === 0 && appliedFilterData.length > 0) {
        this.appliedFilters = appliedFilterData
          .map((filter: any) => this.formatAppliedFilter(filter as any))
          .filter((filter: string) => filter !== '');
      }
      this.persistAppliedFiltersState(this.structuredAppliedFilters.length === 0);
    }

    if (this.widgetId === 'simulation-filters') {
      const entityRow = this.genericTableData.find((row) => row.property === 'Entity');
      const sourceEntity = this.extractSourceEntityFromDetailsResponse(response, detailsData);
      const resolvedEntityValue = this.matchOptionValue(entityRow?.value_options, sourceEntity || entityRow?.value || '');

      if (entityRow && resolvedEntityValue) {
        entityRow.value = resolvedEntityValue;
        this.simulationService.setEntity(resolvedEntityValue);
        this.loadFieldOptionsForSelectedEntity(resolvedEntityValue);
      }

      console.log(this.APPLIED_FILTER_DEBUG_KEY, 'applySimulationDetails.finalDisplay', this.appliedFilters);
      this.syncSimulationData();
      return;
    }

    if (this.widgetId === 'simulation-summary') {
      if (Object.keys(summaryData).length > 0) {
        this.genericTableData = this.buildSummaryRowsFromDetails(summaryData);
      }
      return;
    }

    if (this.widgetId === 'simulation-values') {
      if (priceChangeData.length === 0 || !Array.isArray(this.genericTableData)) {
        return;
      }

      const firstPriceChange = priceChangeData[0];
      const typeRow = this.genericTableData.find((row) => row.property === 'Price Change Type');
      const valueRow = this.genericTableData.find((row) => row.property === 'Price Change Value');
      const basedOnRow = this.genericTableData.find((row) => row.property === 'Based on');

      if (typeRow) {
        typeRow.value = this.matchOptionValue(typeRow?.value_options, firstPriceChange['priceChangeType']);
      }
      if (valueRow && firstPriceChange['priceChangeValue'] !== undefined) {
        valueRow.value = String(firstPriceChange['priceChangeValue'] ?? '');
      }
      if (basedOnRow) {
        basedOnRow.value = this.matchOptionValue(basedOnRow?.value_options, firstPriceChange['basedOn']);
      }

      this.syncSimulationData();
    }
  }

  private toStringRecord(rawObject: any): Record<string, string> {
    if (!rawObject || typeof rawObject !== 'object' || Array.isArray(rawObject)) {
      return {};
    }

    const parsed: Record<string, string> = {};
    Object.entries(rawObject).forEach(([key, value]) => {
      parsed[key] = value === null || value === undefined ? '' : String(value);
    });
    return parsed;
  }

  private parseSerializedObject(rawValue: any): Record<string, string> {
    if (rawValue && typeof rawValue === 'object' && !Array.isArray(rawValue)) {
      return this.toStringRecord(rawValue);
    }

    const input = String(rawValue ?? '').trim();
    if (!input) {
      return {};
    }

    // Handle JSON object strings like {"field":"region","operator":"!=","value":"33 {Brazil}"}
    if (input.startsWith('{') && input.endsWith('}')) {
      try {
        const parsedJson = JSON.parse(input);
        if (parsedJson && typeof parsedJson === 'object' && !Array.isArray(parsedJson)) {
          console.log(this.APPLIED_FILTER_DEBUG_KEY, 'parseSerializedObject.jsonObject', parsedJson);
          return this.toStringRecord(parsedJson);
        }
      } catch {
        // Fall through to legacy key=value parser.
      }
    }

    const normalizedInput = input.replace(/^\{/, '').replace(/\}$/, '');
    const parsed: Record<string, string> = {};
    const pairRegex = /([A-Za-z0-9_]+)=([\s\S]*?)(?=,\s*[A-Za-z0-9_]+=|$)/g;
    let match: RegExpExecArray | null;

    while ((match = pairRegex.exec(normalizedInput)) !== null) {
      parsed[match[1].trim()] = String(match[2] ?? '').trim();
    }

    return parsed;
  }

  private parseSerializedList(rawValue: any): Array<Record<string, string>> {
    if (Array.isArray(rawValue)) {
      return rawValue
        .map((item) => this.parseSerializedObject(item))
        .filter((item) => Object.keys(item).length > 0);
    }

    if (rawValue && typeof rawValue === 'object') {
      const singleRecord = this.toStringRecord(rawValue);
      return Object.keys(singleRecord).length > 0 ? [singleRecord] : [];
    }

    const input = String(rawValue ?? '').trim();
    if (!input) {
      return [];
    }

    // Handle JSON array strings like:
    // [{"field":"region","operator":"!=","value":"33 {Brazil}"}]
    if (input.startsWith('[') && input.endsWith(']')) {
      try {
        const parsedJson = JSON.parse(input);
        if (Array.isArray(parsedJson)) {
          console.log(this.APPLIED_FILTER_DEBUG_KEY, 'parseSerializedList.jsonArray', parsedJson);
          return parsedJson
            .map((item) => this.parseSerializedObject(item))
            .filter((item) => Object.keys(item).length > 0);
        }
      } catch {
        // Fall through to legacy list parser.
      }
    }

    const objects = input.match(/\{[^{}]*\}/g) || [];
    return objects.map((obj) => this.parseSerializedObject(obj));
  }

  private hasMeaningfulFilterValue(value: any): boolean {
    if (Array.isArray(value)) {
      return value.length > 0 && value.some((item) => String(item ?? '').trim() !== '');
    }

    return String(value ?? '').trim() !== '';
  }

  private resolveLogicalOperator(rawValue: any, index: number, total: number): '' | 'AND' | 'OR' {
    if (index >= total - 1) {
      return '';
    }
    const normalized = String(rawValue || '').trim().toUpperCase();
    return normalized === 'OR' ? 'OR' : 'AND';
  }

  private isListOperator(operator: any): boolean {
    const normalized = String(operator || '').trim().toLowerCase().replace(/\s+/g, '_');
    return normalized === 'in' || normalized === 'not_in';
  }

  private formatAppliedFilter(filter: Record<string, string>): string {
    const field = filter['field'] || filter['attributeName'] || '';
    const operator = filter['operator'] || '=';
    const normalizedOperator = String(operator || '=').trim().toLowerCase().replace(/\s+/g, '_');
    const isNullOperator = normalizedOperator === 'is_null' || normalizedOperator === 'is_not_null';
    const expectsListValue = normalizedOperator === 'in' || normalizedOperator === 'not_in';
    const payloadValue = isNullOperator
      ? ''
      : this.toPayloadFilterValue(filter['value'] || filter['attributeValues'] || '', expectsListValue, operator);
    const value = Array.isArray(payloadValue) ? `[${payloadValue.join(', ')}]` : payloadValue;
    if (!field) {
      return '';
    }
    const operatorLabelMap: Record<string, string> = {
      '=': 'Is equal to',
      '!=': 'Is not equal to',
      '>': 'Greater than',
      '>=': 'Greater than or equal to',
      '<': 'Less than',
      '<=': 'Less than or equal to',
      in: 'In',
      not_in: 'Not In',
      'not in': 'Not In',
      contains: 'Contains',
      is_null: 'Is Null',
      is_not_null: 'Is not Null'
    };
    const operatorLabel = operatorLabelMap[normalizedOperator] || operator;
    return value !== '' ? `${field} ${operatorLabel} ${value}` : `${field} ${operatorLabel}`;
  }

  private syncAppliedFiltersDisplayFromStructured(): void {
    this.appliedFilters = (this.structuredAppliedFilters || [])
      .map((filter: any) => {
        const formatted = this.formatAppliedFilter(filter as any);
        if (formatted) {
          return formatted;
        }

        const field = String(filter?.attributeName || filter?.field || '').trim();
        const operator = String(filter?.operator || '=').trim();
        const rawValue = Array.isArray(filter?.attributeValues)
          ? filter.attributeValues.join(', ')
          : String(filter?.attributeValues ?? filter?.value ?? '').trim();

        if (!field) {
          return '';
        }
        return rawValue ? `${field} ${operator} ${rawValue}` : `${field} ${operator}`;
      })
      .filter((filter: string) => filter !== '');
    console.log(this.APPLIED_FILTER_DEBUG_KEY, 'syncDisplay', {
      structuredCount: (this.structuredAppliedFilters || []).length,
      displayCount: (this.appliedFilters || []).length,
      display: this.appliedFilters
    });
  }

  private getAppliedFiltersStorageKey(): string {
    const uuid = String(this.getSelectedSimulationUuid() || this.simulationService.getLastRunUuid() || '').trim();
    return `simulation_applied_filters_${uuid}`;
  }

  private getAppliedFiltersStorageKeyByUuid(uuid: string): string {
    const normalizedUuid = String(uuid || '').trim();
    return `simulation_applied_filters_${normalizedUuid}`;
  }

  private getAppliedFilterDialogStateStorageKey(): string {
    const uuid = String(this.getSelectedSimulationUuid() || this.simulationService.getLastRunUuid() || '').trim();
    return `simulation_applied_filter_dialog_state_${uuid}`;
  }

  private getAppliedFilterDialogStateStorageKeyByUuid(uuid: string): string {
    const normalizedUuid = String(uuid || '').trim();
    return `simulation_applied_filter_dialog_state_${normalizedUuid}`;
  }

  private persistCurrentServiceAppliedFiltersForUuid(uuid?: string): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    const filters = Array.isArray(this.simulationService.getAppliedFilters())
      ? this.simulationService.getAppliedFilters()
      : [];
    if (filters.length === 0) {
      return;
    }

    const normalizedUuid = String(uuid || this.getSelectedSimulationUuid() || this.simulationService.getLastRunUuid() || '').trim();
    if (!normalizedUuid) {
      return;
    }
    const storageKey = this.getAppliedFiltersStorageKeyByUuid(normalizedUuid);
    try {
      localStorage.setItem(storageKey, JSON.stringify(filters));
      if (this.appliedFilterDialogState && typeof this.appliedFilterDialogState === 'object') {
        const serializedState = JSON.stringify(this.appliedFilterDialogState);
        localStorage.setItem(this.getAppliedFilterDialogStateStorageKeyByUuid(normalizedUuid), serializedState);
      }
    } catch (err) {
      console.warn('Failed to persist service applied filters for save flow', err);
    }
  }

  private persistAppliedFiltersState(allowEmpty = false): void {
    const filtersToPersist = this.structuredAppliedFilters || [];
    if (!allowEmpty && filtersToPersist.length === 0) {
      console.log(this.APPLIED_FILTER_DEBUG_KEY, 'persistSkippedEmpty', { allowEmpty });
      return;
    }

    console.log(this.APPLIED_FILTER_DEBUG_KEY, 'persistState', {
      allowEmpty,
      count: filtersToPersist.length,
      filters: filtersToPersist
    });
    this.simulationService.setAppliedFilters(filtersToPersist);
    this.persistAppliedFiltersLayoutState();

    if (typeof localStorage === 'undefined') {
      return;
    }

    try {
      const activeUuid = String(this.getSelectedSimulationUuid() || this.simulationService.getLastRunUuid() || '').trim();
      if (!activeUuid) {
        return;
      }
      localStorage.setItem(this.getAppliedFiltersStorageKeyByUuid(activeUuid), JSON.stringify(filtersToPersist));
      const dialogStateStorageKey = this.getAppliedFilterDialogStateStorageKeyByUuid(activeUuid);
      if (filtersToPersist.length === 0) {
        localStorage.removeItem(dialogStateStorageKey);
      } else if (this.appliedFilterDialogState && typeof this.appliedFilterDialogState === 'object') {
        const serializedState = JSON.stringify(this.appliedFilterDialogState);
        localStorage.setItem(dialogStateStorageKey, serializedState);
      }
      console.log(this.APPLIED_FILTER_DEBUG_KEY, 'persistLocalStorage', {
        key: this.getAppliedFiltersStorageKeyByUuid(activeUuid),
        count: filtersToPersist.length
      });
    } catch (err) {
      console.warn('Failed to persist applied filters to local storage', err);
    }
  }

  private restoreAppliedFiltersStateFromStorage(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    try {
      const activeUuid = String(this.getSelectedSimulationUuid() || this.simulationService.getLastRunUuid() || '').trim();
      if (!activeUuid) {
        this.appliedFilterDialogState = null;
        return;
      }
      const raw = localStorage.getItem(this.getAppliedFiltersStorageKeyByUuid(activeUuid));
      const rawDialogState = localStorage.getItem(this.getAppliedFilterDialogStateStorageKeyByUuid(activeUuid));
      this.appliedFilterDialogState = rawDialogState ? JSON.parse(rawDialogState) : null;
      console.log(this.APPLIED_FILTER_DEBUG_KEY, 'restoreLocalStorage.raw', {
        key: this.getAppliedFiltersStorageKeyByUuid(activeUuid),
        raw
      });
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        return;
      }
      if (parsed.length === 0) {
        this.structuredAppliedFilters = [];
        this.appliedFilters = [];
        this.appliedFiltersEditMode = false;
        this.simulationService.setAppliedFilters([]);
        this.persistAppliedFiltersLayoutState();
        return;
      }

      this.structuredAppliedFilters = parsed;
      this.syncAppliedFiltersDisplayFromStructured();
      this.simulationService.setAppliedFilters(this.structuredAppliedFilters);
      this.persistAppliedFiltersLayoutState();
      console.log(this.APPLIED_FILTER_DEBUG_KEY, 'restoreLocalStorage.applied', {
        count: this.structuredAppliedFilters.length,
        display: this.appliedFilters
      });
    } catch (err) {
      console.warn('Failed to restore applied filters from local storage', err);
    }
  }

  private extractSourceEntityFromDetailsResponse(response: any, detailsData: any): string {
    const candidates = [
      detailsData?.source_entity,
      detailsData?.sourceEntity,
      response?.responseList?.[0]?.simulation?.[0]?.source_entity,
      response?.responseList?.[0]?.source_entity,
      response?.source_entity,
      response?.requestData?.source_entity
    ];

    for (const candidate of candidates) {
      const value = String(candidate ?? '').trim();
      if (value) {
        return value;
      }
    }
    return '';
  }

  private buildSummaryRowsFromDetails(summary: Record<string, string>): any[] {
    return [
      // Part details
      { metric: 'Total # Records', value: summary['totalParts'] || '' },
      { metric: '# Parts Impacted', value: summary['totalPartsImpacted'] || '' },
      // Current Status
      { metric: 'Current Annual Units', value: summary['totalCurrentAnnualUnits'] || '' },
      { metric: 'Current Sales', value: summary['totalCurrentSalesValue'] || '' },
      { metric: 'Current Future Sales', value: summary['totalCurrentFutureSales'] || '' },
      { metric: 'Current Impact (USD)', value: summary['totalCurrentImpact'] || '' },
      { metric: 'Current Impact %', value: summary['totalCurrentImpactPercent'] || '' },
      // Simulated
      { metric: 'Simulated Future Sales', value: summary['simulatedFutureSales'] || summary['totalFutureSalesValue'] || '' },
      { metric: 'Simulated Impact (USD)', value: summary['simulatedImpactUSD'] || summary['totalDollarImpact'] || '' },
      { metric: 'Simulated Impact %', value: summary['simulatedImpactPercent'] || summary['totalImpactResult'] || '' },
      // Summary
      { metric: 'Total Impact (USD)', value: summary['totalImpactUsd'] || summary['totalDollarImpact'] || '' },
      { metric: 'Total Impact %', value: summary['totalOverallImpactPercent'] || summary['totalImpactPercent'] || summary['totaloverallImpact'] || '' }
    ];
  }

  private normalizeOptionToken(value: any): string {
    return String(value ?? '').toLowerCase().replace(/[_\s-]+/g, '').trim();
  }

  private matchOptionValue(options: any[] | undefined, rawValue: any): string {
    const targetValue = String(rawValue ?? '').trim();
    if (!targetValue) {
      return '';
    }

    if (!Array.isArray(options) || options.length === 0) {
      return targetValue;
    }

    const normalizedTarget = this.normalizeOptionToken(targetValue);
    const exactMatch = options.find((opt) => this.normalizeOptionToken(opt) === normalizedTarget);
    if (exactMatch !== undefined) {
      return String(exactMatch);
    }

    const containsMatch = options.find((opt) => {
      const normalizedOption = this.normalizeOptionToken(opt);
      return normalizedOption.includes(normalizedTarget) || normalizedTarget.includes(normalizedOption);
    });

    return containsMatch !== undefined ? String(containsMatch) : targetValue;
  }

  private formatDateOnly(value: any): string {
    const rawValue = String(value ?? '').trim();
    if (!rawValue) {
      return '';
    }

    const isoPrefixMatch = rawValue.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoPrefixMatch) {
      return isoPrefixMatch[1];
    }

    const parsedDate = new Date(rawValue);
    if (Number.isNaN(parsedDate.getTime())) {
      return rawValue;
    }

    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private toSortableTimestamp(value: any): number {
    const normalized = this.formatDateOnly(value);
    if (!normalized) {
      return 0;
    }
    const timestamp = new Date(normalized).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  private normalizeSimulationTableRows(rows: any[]): any[] {
    if (!Array.isArray(rows)) {
      return [];
    }

    const mappedRows = rows.map((item: any) => ({
      ...item,
      created_date: this.formatDateOnly(item.created_date),
      last_execution_date: this.formatDateOnly(item.last_execution_date),
      current_units: item.current_units ?? item.current_annual_units,
      impact_percent: item.impact_percent ?? item.impact_percentage
    }));

    mappedRows.sort((a: any, b: any) => {
      const aId = Number(a?.sim_deep_id);
      const bId = Number(b?.sim_deep_id);
      const aValid = Number.isFinite(aId);
      const bValid = Number.isFinite(bId);

      if (aValid && bValid && aId !== bId) {
        return bId - aId;
      }
      if (aValid !== bValid) {
        return aValid ? -1 : 1;
      }

      const createdDiff = this.toSortableTimestamp(b?.created_date) - this.toSortableTimestamp(a?.created_date);
      if (createdDiff !== 0) {
        return createdDiff;
      }

      const lastExecDiff = this.toSortableTimestamp(b?.last_execution_date) - this.toSortableTimestamp(a?.last_execution_date);
      if (lastExecDiff !== 0) {
        return lastExecDiff;
      }

      return String(a?.name || '').localeCompare(String(b?.name || ''));
    });

    return mappedRows.map((row: any, index: number) => ({
      ...row,
      original_sim_deep_id: row?.sim_deep_id,
      sim_deep_id: index + 1
    }));
  }

  private loadGenericTableData(): void {
    if (!this.data?.apiUrl) return;
    this.genericLoading = true;

    let fetchObs: Observable<any>;
    if (this.widgetId === 'simulation-table') {
      fetchObs = this.priceFileSimulationService.getSimulationList();
    } else if (this.widgetId === 'simulation-summary') {
      fetchObs = this.priceFileSimulationService.getSimulationSummary(this.getSelectedSimulationUuid());
    } else {
      fetchObs = this.apiConfigService.getApi(this.data.apiUrl, null, this.data, this.data?.method || 'get');
    }

    fetchObs.subscribe({
      next: (res: any) => {
        const key = this.data?.key || 'data';
        const responseData = res?.[key] || res;

        // Extract applied filters from API response (camelCase or snake_case)
        if (Array.isArray(res?.appliedFilters)) {
          this.appliedFilters = [...res.appliedFilters];
        } else if (res?.applied_filters !== undefined && res?.applied_filters !== null) {
          const parsedFilters = this.parseSerializedList(res.applied_filters);
          this.appliedFilters = parsedFilters
            .map((filter: any) => this.formatAppliedFilter(filter as any))
            .filter((filter: string) => filter !== '');
        }

        if (Array.isArray(responseData)) {
          // For simulation-table always render backend response as source of truth.
          const mappedData = this.widgetId === 'simulation-table'
            ? this.normalizeSimulationTableRows(responseData)
            : [...responseData];

          this.genericTableData = mappedData;
          this.originalGenericData = [...mappedData]; // Backup
          this.extractUniqueValues();
        }
        this.genericLoading = false;
        if (this.widgetId === 'simulation-summary') {
          this.loadSelectedSimulationDetails();
        }
      },
      error: (err: any) => {
        this.genericLoading = false;
        const errorMessage = (err.error && typeof err.error === 'string')
          ? err.error
          : (err.error?.message || err.message || 'An unknown error occurred.');
        this.snackBar.open(errorMessage, 'Close', {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'bottom',
          panelClass: ['error-snackbar']
        });
      }
    });
  }

  getColumnLabel(columnKey: string): string {
    const found = this.genericColumns.find(col => col.key === columnKey);
    return found ? found.label : columnKey;
  }

  getGenericCellDisplayValue(row: any, col: any): string {
    const rawValue = row?.[col?.key];
    const context = `${col?.key || ''} ${col?.label || ''} ${row?.metric || ''} ${row?.property || ''}`;
    return formatDisplayValueByContext(rawValue, context);
  }

  // Preferred display order for the simulation summary sections.
  private readonly summarySectionOrder: string[] = ['Part details', 'Current Status', 'Simulated', 'Summary'];

  private classifySummaryMetric(metric: string): string {
    const m = String(metric || '').toLowerCase();
    if (m.includes('record') || m.includes('parts impacted') || m.includes('# parts')) {
      return 'Part details';
    }
    if (m.startsWith('current')) {
      return 'Current Status';
    }
    if (m.startsWith('estimated') || m.startsWith('simulated')) {
      return 'Simulated';
    }
    if (m.includes('overall') || m.includes('total impact')) {
      return 'Summary';
    }
    return 'Summary';
  }

  /**
   * Groups the flat summary rows into labelled sections so the table can render
   * section headers with visual gaps between groups (see price-file-simulation).
   */
  getSummarySections(): { title: string; rows: any[] }[] {
    const rows = Array.isArray(this.genericTableData) ? this.genericTableData : [];
    const grouped = new Map<string, any[]>();

    for (const row of rows) {
      const section = this.classifySummaryMetric(row?.metric);
      if (!grouped.has(section)) {
        grouped.set(section, []);
      }
      grouped.get(section)!.push(row);
    }

    // Keep the preferred order first, then any unexpected sections afterwards.
    const orderedTitles = [
      ...this.summarySectionOrder.filter((title) => grouped.has(title)),
      ...Array.from(grouped.keys()).filter((title) => !this.summarySectionOrder.includes(title))
    ];

    return orderedTitles.map((title) => ({ title, rows: grouped.get(title)! }));
  }

  onFilterInput(columnKey: string, event: any): void {
    this.filterValues[columnKey] = event.target.value?.toLowerCase() || '';
    this.filterDataLocally();
  }

  onGenericCellEdit(row: any, columnKey: string, event: any): void {
    row[columnKey] = event.target.value;
    this.syncSimulationData();
  }

  onCellClick(row: any, col: any): void {
    if (col.type === 'link' && col.action === 'navigate' && col.route) {
      const routePath = this.normalizeRoutePath(col.route);
      if (this.widgetId === 'simulation-table' && routePath === '/price-file-simulation/detail') {
        // Prevent previous simulation filters from leaking into the next detail screen.
        this.appliedFilters = [];
        this.structuredAppliedFilters = [];
        this.simulationService.setAppliedFilters([]);
      }
      this.router.navigate([col.route], { queryParams: row });
    }
  }

  isActionRouteActive(action: any): boolean {
    if (!action?.route) {
      return false;
    }

    const currentPath = this.normalizeRoutePath(this.router.url.split('?')[0]);
    const targetPath = this.normalizeRoutePath(String(action.route).split('?')[0]);

    if (action.matchSubRoutes) {
      return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
    }

    return currentPath === targetPath;
  }

  getActionButtonClasses(action: any): string {
    const baseClass = action?.class || 'action-button-black';
    return this.isActionRouteActive(action) ? `${baseClass} active-route-action` : baseClass;
  }

  getTopActions(): any[] {
    const actions = Array.isArray(this.data?.actions) ? this.data.actions : [];
    if (!actions.length) {
      return [];
    }

    const hasPerActionPosition = actions.some((action: any) => action?.position || action?.actionsPosition);
    if (!hasPerActionPosition) {
      return this.data?.actionsPosition === 'bottom'
        ? []
        : actions.filter((action: any) => this.shouldShowAction(action));
    }

    return actions.filter((action: any) =>
      this.resolveActionPosition(action) === 'top' && this.shouldShowAction(action)
    );
  }

  getBottomActions(): any[] {
    const actions = Array.isArray(this.data?.actions) ? this.data.actions : [];
    if (!actions.length) {
      return [];
    }

    const hasPerActionPosition = actions.some((action: any) => action?.position || action?.actionsPosition);
    if (!hasPerActionPosition) {
      return this.data?.actionsPosition === 'top'
        ? []
        : actions.filter((action: any) => this.shouldShowAction(action));
    }

    return actions.filter((action: any) =>
      this.resolveActionPosition(action) === 'bottom' && this.shouldShowAction(action)
    );
  }

  shouldShowGenericLabelRow(): boolean {
    if (!Array.isArray(this.genericColumns) || this.genericColumns.length === 0) {
      return false;
    }

    if (!this.data?.hideColumnFilters) {
      return true;
    }

    return this.genericColumns.some((col: any) => String(col?.label || '').trim() !== '');
  }

  private resolveActionPosition(action: any): 'top' | 'bottom' {
    const rawPosition = String(action?.position || action?.actionsPosition || 'top').toLowerCase().trim();
    return rawPosition === 'bottom' ? 'bottom' : 'top';
  }

  private shouldShowAction(action: any): boolean {
    if (action?.hideWhenSelectionMissing === true && action?.requireSelectedRow === true) {
      return this.selectedRows.size > 0;
    }

    return true;
  }

  isActionDisabled(action: any): boolean {
    const actionType = String(action?.action || '').toLowerCase();
    const requiresFilters = actionType === 'run';
    if (requiresFilters && !this.hasAnyAppliedFilters()) {
      return true;
    }

    if (actionType === 'run') {
      // In simulation-values, allow Run only for numeric Price Change Value.
      if (this.widgetId !== 'simulation-values') {
        return false;
      }
      return !this.isPriceChangeValueNumeric();
    }

    return false;
  }

  isFilterAction(action: any): boolean {
    return String(action?.action || '').toLowerCase() === 'filter';
  }

  private isPriceChangeValueNumeric(): boolean {
    if (!Array.isArray(this.genericTableData)) {
      return false;
    }

    const valueRow = this.genericTableData.find((row: any) => row?.property === 'Price Change Value');
    const rawValue = String(valueRow?.value ?? '').trim();
    if (!rawValue) {
      return false;
    }

    return /^-?(?:\d+|\d+\.\d+|\.\d+)$/.test(rawValue);
  }

  private hasAnyAppliedFilters(): boolean {
    const localFilters = Array.isArray(this.structuredAppliedFilters) ? this.structuredAppliedFilters : [];
    if (localFilters.length > 0) {
      return true;
    }

    const sharedFilters = this.simulationService.getAppliedFilters();
    return Array.isArray(sharedFilters) && sharedFilters.length > 0;
  }

  hasNoAppliedFilters(): boolean {
    return !this.hasAnyAppliedFilters();
  }

  handleGenericAction(action: any): void {
    const hasRoute = !!action?.route;
    const hasApi = !!action?.apiUrl;
    const actionType = String(action?.action || '').toLowerCase();

    if (actionType === 'api' && hasApi) {
      const apiAction = { ...action };
      if (hasRoute && apiAction.navigateAfterApi === undefined) {
        apiAction.navigateAfterApi = true;
      }
      this.handleGenericApiAction(apiAction);
      return;
    }

    if (actionType === 'navigate' && hasRoute) {
      this.navigateWithSelectedRow(action);
      return;
    }

    if (actionType === 'add') {
      this.handleAddFilter();
    } else if (actionType === 'run') {
      if (this.isActionDisabled(action)) {
        this.snackBar.open('Run is enabled only when Price Change Value is a valid number.', 'Close', {
          duration: 3500,
          horizontalPosition: 'right',
          verticalPosition: 'bottom',
          panelClass: ['error-snackbar']
        });
        return;
      }
      this.handleRunSimulation();
    } else if (actionType === 'filter') {
      this.handleFilterAction();
    } else if (actionType === 'reset') {
      this.handleResetGeneric();
    } else if (actionType === 'clear') {
      this.handleClearGeneric();
    } else if (actionType === 'save-simulation' || actionType === 'save') {
      this.handleSaveSimulation();
    } else {
      console.log('Unknown action triggered:', action);
    }
  }

  private handleFilterAction(): void {
    // Always hydrate latest persisted filter/dialog state before opening the dialog.
    // This guards against stale in-memory state after route navigation.
    this.restoreAppliedFiltersStateFromStorage();

    const selectedEntityRow = this.genericTableData.find((row: any) => String(row?.property || '').trim().toLowerCase() === 'entity');
    const selectedEntity = String(selectedEntityRow?.value || this.simulationService.getEntity() || '').trim();
    const selectedUuid = this.getSelectedSimulationUuid();
    const { fields, operators } = this.priceFileSimulationService.getCachedFilterDialogOptions(selectedEntity, selectedUuid);
    this.openFilterDialog(fields, operators, selectedEntity, selectedUuid);
  }

  private openFilterDialog(fieldOptions: string[] = [], operatorOptions: string[] = [], selectedEntity = '', selectedUuid = ''): void {
    // Preserve explicit empty state: if local structured filters are [], do not fall back
    // to service filters, otherwise deleted filters can reappear in the dialog.
    const existingFilters = Array.isArray(this.structuredAppliedFilters)
      ? this.structuredAppliedFilters
      : (Array.isArray(this.simulationService.getAppliedFilters()) ? this.simulationService.getAppliedFilters() : []);
    console.log(this.APPLIED_FILTER_DEBUG_KEY, 'openFilterDialog.initialFilters', existingFilters);

    const dialogRef = this.dialog.open(FilterDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      panelClass: 'custom-dialog-container',
      data: {
        tableColumns: this.genericColumns,
        fieldOptions,
        operatorOptions,
        selectedEntity,
        selectedUuid,
        initialFilters: existingFilters,
        initialFormState: this.appliedFilterDialogState
      }
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      console.log(this.APPLIED_FILTER_DEBUG_KEY, 'openFilterDialog.afterClosed.result', result);
      const hasScopedAppliedFilters = Array.isArray(result?.appliedFilters);
      if (!hasScopedAppliedFilters && (!result?.filters || !Array.isArray(result.filters))) {
        return;
      }

      if (hasScopedAppliedFilters) {
        this.structuredAppliedFilters = result.appliedFilters
          .map((filter: any, index: number, all: any[]) => {
            const mappedOperator = this.mapDialogOperatorToSymbol(filter?.operator);
            const normalizedOperatorType = String(this.mapOperatorTypeFromSymbol(mappedOperator) || '')
              .trim()
              .toUpperCase()
              .replace(/\s+/g, '_');
            const forceArray = this.isListOperator(mappedOperator);
            const payloadKey = this.mapDialogFieldToPayloadKey(filter?.field);
            const value = this.toPayloadFilterValue(filter?.value, forceArray, mappedOperator);
            const rawLogical = String(filter?.logicalOperator || '').trim().toUpperCase();
            const logicalOperator = index === all.length - 1
              ? ''
              : (rawLogical === 'OR' ? 'OR' : 'AND');

            return {
              _operatorType: normalizedOperatorType,
              attributeName: payloadKey,
              field: payloadKey,
              operator: mappedOperator,
              attributeValues: value,
              value: value,
              multiValueInput: forceArray,
              logicalOperator,
              beginScope: String(filter?.beginScope || ''),
              endScope: String(filter?.endScope || '')
            };
          })
          .filter((filter: any) =>
            String(filter.attributeName || '').trim() !== '' && (
              filter._operatorType === 'IS_NULL'
              || filter._operatorType === 'IS_NOT_NULL'
              || this.hasMeaningfulFilterValue(filter.attributeValues)
            )
          )
          .map(({ _operatorType, ...filter }: any) => filter as any);
      } else {
        const flattenedConditions = this.extractConditionRules(result.filters);
        this.structuredAppliedFilters = flattenedConditions
          .map((condition, index, all) => {
            const normalizedOperatorType = String(condition.operatorType || '').trim().toUpperCase().replace(/\s+/g, '_');
            const mappedOperator = this.mapDialogOperatorToSymbol(condition.operatorType);
            const forceArray = this.isListOperator(mappedOperator);
            return {
              _operatorType: normalizedOperatorType,
              attributeName: this.mapDialogFieldToPayloadKey(condition.key),
              operator: mappedOperator,
              attributeValues: this.toPayloadFilterValue(condition.value, forceArray, mappedOperator),
              value: this.toPayloadFilterValue(condition.value, forceArray, mappedOperator),
              multiValueInput: forceArray,
              logicalOperator: this.resolveLogicalOperator(condition?.logicalOperator ?? condition?.joinType, index, all.length)
            };
          })
          .filter((filter) =>
            String(filter.attributeName || '').trim() !== '' && (
              filter._operatorType === 'IS_NULL'
              || filter._operatorType === 'IS_NOT_NULL'
              || this.hasMeaningfulFilterValue(filter.attributeValues)
            )
          )
          .map(({ _operatorType, ...filter }) => filter as any);
      }
      console.log(this.APPLIED_FILTER_DEBUG_KEY, 'openFilterDialog.structuredAfterApply', this.structuredAppliedFilters);

      if (result?.formState && typeof result.formState === 'object') {
        this.appliedFilterDialogState = result.formState;
      }
      if (this.structuredAppliedFilters.length === 0) {
        this.structuredAppliedFilters = [];
        this.appliedFilters = [];
        this.appliedFiltersEditMode = false;
        this.appliedFilterDialogState = null;
        this.persistAppliedFiltersState(true);
        return;
      }
      this.syncAppliedFiltersDisplayFromStructured();
      this.persistAppliedFiltersState();
    });
  }

  private persistAppliedFiltersLayoutState(): void {
    if (!this.data || typeof this.data !== 'object') {
      return;
    }
    this.data.appliedFilters = [...(this.appliedFilters || [])];
  }

  private extractConditionRules(filters: any[]): any[] {
    const conditions: any[] = [];
    const stack = Array.isArray(filters) ? [...filters] : [];

    while (stack.length > 0) {
      const item = stack.shift();
      if (!item) {
        continue;
      }

      if (item.filterType === 'CONDITIONGROUP' && Array.isArray(item.filters)) {
        stack.unshift(...item.filters);
        continue;
      }

      if (item.filterType === 'CONDITION') {
        conditions.push(item);
      }
    }

    return conditions;
  }

  private mapDialogOperatorToSymbol(operatorType: string): string {
    const normalizedOperator = String(operatorType || '').trim().toUpperCase().replace(/\s+/g, '_');
    const operatorMap: Record<string, string> = {
      EQUALS: '=',
      IS_EQUAL_TO: '=',
      IS_NOT_EQUAL: '!=',
      IS_NOT_EQUAL_TO: '!=',
      GREATER_THAN: '>',
      LESS_THAN: '<',
      GREATER_THAN_OR_EQUAL: '>=',
      LESS_THAN_OR_EQUAL: '<=',
      CONTAINS: 'contains',
      IN: 'in',
      NOT_IN: 'not_in',
      IS_NULL: 'is_null',
      IS_NOT_NULL: 'is_not_null'
    };
    return operatorMap[normalizedOperator] || (normalizedOperator ? normalizedOperator.toLowerCase() : '=');
  }

  private mapOperatorTypeFromSymbol(operator: string): string {
    const normalized = String(operator || '').trim().toLowerCase().replace(/\s+/g, '_');
    const reverseMap: Record<string, string> = {
      '=': 'EQUALS',
      '!=': 'IS_NOT_EQUAL',
      '>': 'GREATER_THAN',
      '<': 'LESS_THAN',
      '>=': 'GREATER_THAN_OR_EQUAL',
      '<=': 'LESS_THAN_OR_EQUAL',
      contains: 'CONTAINS',
      in: 'IN',
      not_in: 'NOT_IN',
      is_null: 'IS_NULL',
      is_not_null: 'IS_NOT_NULL'
    };
    return reverseMap[normalized] || 'EQUALS';
  }

  private mapDialogFieldToPayloadKey(field: any): string {
    const normalizedField = String(field ?? '').trim().toLowerCase();
    if (!normalizedField) {
      return '';
    }

    const explicitMap: Record<string, string> = {
      code: 'code',
      region: 'region',
      pricing_admin_owner: 'pricing_admin_owner',
      'pricing admin owner': 'pricing_admin_owner',
      product_bu: 'product_bu',
      'product bu': 'product_bu',
      segment: 'segment',
      segment_type: 'segment_type',
      'segment type': 'segment_type',
      saleable: 'saleable',
      current_price: 'current_price',
      'current price': 'current_price',
      future_price: 'future_price',
      'future price': 'future_price',
      publish_price: 'publish_price',
      'publish price': 'publish_price',
      current_effective_date: 'current_effective_date',
      'current effective date': 'current_effective_date',
      publish_effective_date: 'publish_effective_date',
      'publish effective date': 'publish_effective_date',
      future_effective_date: 'future_effective_date',
      'future effective date': 'future_effective_date',
      pricing_action: 'pricing_action',
      'pricing action': 'pricing_action',
      change_reason: 'change_reason',
      'change reason': 'change_reason',
      brand: 'brand',
      part_owner: 'part_owner',
      'part owner': 'part_owner',
      regional_pricing_flag: 'regional_pricing_flag',
      'regional pricing flag': 'regional_pricing_flag',
      effective_pvc_code: 'effective_pvc_code',
      'effective pvc code': 'effective_pvc_code',
      wpt: 'wpt',
      engine_model: 'engine_model',
      'engine model': 'engine_model',
      ecc: 'ecc',
      swapp: 'swapp',
      information: 'information',
      part_number: 'part_number',
      'part number': 'part_number'
    };

    if (explicitMap[normalizedField]) {
      return explicitMap[normalizedField];
    }

    return normalizedField
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  private normalizeRoutePath(path: string): string {
    if (!path) {
      return '/';
    }

    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return normalizedPath.length > 1 ? normalizedPath.replace(/\/+$/, '') : normalizedPath;
  }

  private getAppliedFiltersForDeepDiveRequest(): Array<{
    field: string;
    operator: string;
    value: string | string[];
    logicalOperator?: '' | 'AND' | 'OR';
    beginScope?: string;
    endScope?: string;
  }> {
    const APPLIED_FILTER_TRACE_KEY = '[APPLIED_FILTER_TRACE]';
    const normalizeLogicalOperator = (value: any): '' | 'AND' | 'OR' => {
      const op = String(value || '').trim().toUpperCase();
      return op === 'AND' || op === 'OR' ? op : '';
    };
    const sourceFilters = this.simulationService.getAppliedFilters() || [];
    const mappedFilters = sourceFilters
      .map((filter: any) => {
        const operator = filter?.operator || '=';
        return {
          field: filter?.attributeName || filter?.field || '',
          operator,
          value: this.toPayloadFilterValue(
            Array.isArray(filter?.attributeValues) ? filter.attributeValues : filter?.value,
            filter?.multiValueInput === true,
            operator
          ),
          logicalOperator: normalizeLogicalOperator(filter?.logicalOperator),
          beginScope: String(filter?.beginScope || ''),
          endScope: String(filter?.endScope || '')
        };
      })
      .filter((filter: any) => String(filter.field || '').trim() !== '');
    console.log(APPLIED_FILTER_TRACE_KEY, 'editableTable.getAppliedFiltersForDeepDiveRequest', {
      sourceFilters,
      mappedFilters
    });
    return mappedFilters;
  }

  private getAppliedFiltersForDeepDiveFromDetailsResponse(response: any): Array<{
    field: string;
    operator: string;
    value: string | string[];
    logicalOperator?: '' | 'AND' | 'OR';
    beginScope?: string;
    endScope?: string;
  }> {
    const APPLIED_FILTER_TRACE_KEY = '[APPLIED_FILTER_TRACE]';
    const normalizeLogicalOperator = (value: any): '' | 'AND' | 'OR' => {
      const op = String(value || '').trim().toUpperCase();
      return op === 'AND' || op === 'OR' ? op : '';
    };
    const detailsData = response?.responseList?.[0]?.simulation?.[0]?.data;
    const appliedFiltersRaw =
      detailsData?.appliedFilters
      ?? detailsData?.applied_filters
      ?? response?.appliedFilters
      ?? response?.applied_filters
      ?? response?.requestData?.appliedFilters
      ?? response?.requestData?.applied_filters;
    const appliedFilterData = this.parseSerializedList(appliedFiltersRaw);
    const structuredAppliedFilters = appliedFilterData
      .map((filter, index) => {
        const payloadKey = filter['field'] || filter['attributeName'] || '';
        const operator = filter['operator'] || '=';
        const attributeValues = this.normalizeManualFilterValues(filter['value'] ?? filter['attributeValues'] ?? '', false);
        return {
          attributeName: payloadKey,
          field: payloadKey,
          operator,
          attributeValues,
          value: attributeValues.length <= 1 ? (attributeValues[0] ?? '') : attributeValues,
          multiValueInput: this.isListOperator(operator),
          logicalOperator: this.resolveLogicalOperator(filter?.['logicalOperator'], index, appliedFilterData.length),
          beginScope: String(filter?.['beginScope'] || ''),
          endScope: String(filter?.['endScope'] || '')
        };
      })
      .filter((filter) =>
        filter.attributeName !== '' && this.hasMeaningfulFilterValue(filter.attributeValues)
      );
    console.log(APPLIED_FILTER_TRACE_KEY, 'editableTable.getAppliedFiltersForDeepDiveFromDetailsResponse.structuredAppliedFilters', structuredAppliedFilters);

    if (structuredAppliedFilters.length > 0) {
      this.simulationService.setAppliedFilters(structuredAppliedFilters);
    }

    const mappedFilters = structuredAppliedFilters
      .map((filter: any) => {
        const operator = filter?.operator || '=';
        return {
          field: filter?.attributeName || '',
          operator,
          value: this.toPayloadFilterValue(
            Array.isArray(filter?.attributeValues) ? filter.attributeValues : filter?.value,
            filter?.multiValueInput === true,
            operator
          ),
          logicalOperator: normalizeLogicalOperator(filter?.logicalOperator),
          beginScope: String(filter?.beginScope || ''),
          endScope: String(filter?.endScope || '')
        };
      })
      .filter((filter: any) => String(filter.field || '').trim() !== '');
    console.log(APPLIED_FILTER_TRACE_KEY, 'editableTable.getAppliedFiltersForDeepDiveFromDetailsResponse.mappedFilters', mappedFilters);
    return mappedFilters;
  }

  private getPriceChangesForDeepDiveFromDetailsResponse(response: any): Array<{ priceChangeType: string; priceChangeValue: string; basedOn: string }> {
    const detailsData = response?.responseList?.[0]?.simulation?.[0]?.data;
    const priceChangeData = this.parseSerializedList(detailsData?.priceChanges);

    return priceChangeData
      .map((priceChange) => ({
        priceChangeType: String(priceChange['priceChangeType'] || '').trim(),
        priceChangeValue: String(priceChange['priceChangeValue'] || '').trim(),
        basedOn: String(priceChange['basedOn'] || 'current_price').trim() || 'current_price'
      }))
      .filter((priceChange) => priceChange.priceChangeValue !== '');
  }

  private navigateWithSelectedRow(action: any): void {
    const shouldSendSelectedRow = action.passSelectedRowToRoute !== false;
    const requireSelectedRow = action.requireSelectedRow === true;
    const attachSelectedSimulationUuid = action.attachSelectedSimulationUuid === true;
    const fetchDeepDiveDetailsBeforeNavigate = action.fetchDeepDiveDetailsBeforeNavigate === true;
    const includeAppliedFiltersInDeepDiveRequest = action.includeAppliedFiltersInDeepDiveRequest === true;
    const selectedRow = this.selectedRows.size > 0 ? Array.from(this.selectedRows)[0] : null;

    if (requireSelectedRow && !selectedRow) {
      this.snackBar.open('Please select a simulation row first.', 'Close', {
        duration: 3000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom',
        panelClass: ['error-snackbar']
      });
      return;
    }

    let queryParams: any = shouldSendSelectedRow && selectedRow
      ? { ...selectedRow }
      : { ...(this.route.snapshot.queryParams || {}) };
    if (action?.queryParams && typeof action.queryParams === 'object') {
      queryParams = {
        ...queryParams,
        ...action.queryParams
      };
    }
    let resolvedUuid = '';

    if (attachSelectedSimulationUuid && selectedRow) {
      resolvedUuid = this.resolveSimulationUuid(selectedRow);
      if (!resolvedUuid) {
        this.snackBar.open('Selected simulation UUID is missing.', 'Close', {
          duration: 3000,
          horizontalPosition: 'right',
          verticalPosition: 'bottom',
          panelClass: ['error-snackbar']
        });
        return;
      }

      queryParams = {
        ...queryParams,
        uuid: resolvedUuid,
        quote_uuid: queryParams?.quote_uuid || resolvedUuid
      };
    } else if (attachSelectedSimulationUuid) {
      resolvedUuid = this.resolveSimulationUuid(queryParams);
      if (resolvedUuid) {
        queryParams = {
          ...queryParams,
          uuid: queryParams?.uuid || resolvedUuid,
          quote_uuid: queryParams?.quote_uuid || resolvedUuid
        };
      }
    }

    if (fetchDeepDiveDetailsBeforeNavigate) {
      if (!resolvedUuid) {
        resolvedUuid = this.resolveSimulationUuid(queryParams);
      }

      if (!resolvedUuid) {
        this.snackBar.open('Selected simulation UUID is missing.', 'Close', {
          duration: 3000,
          horizontalPosition: 'right',
          verticalPosition: 'bottom',
          panelClass: ['error-snackbar']
        });
        return;
      }

      this.isLoading = true;
      const deepDiveRequest$ = includeAppliedFiltersInDeepDiveRequest
        ? this.priceFileSimulationService.getSimulationDetails(resolvedUuid).pipe(
          switchMap((detailsResponse: any) => {
            const userSelectedFilters = this.getAppliedFiltersForDeepDiveRequest();
            const refreshedFilters = this.getAppliedFiltersForDeepDiveFromDetailsResponse(detailsResponse);
            const refreshedPriceChanges = this.getPriceChangesForDeepDiveFromDetailsResponse(detailsResponse);
            const appliedFilters = userSelectedFilters.length > 0
              ? userSelectedFilters
              : refreshedFilters;

            return this.priceFileSimulationService.getDeepDiveDetails(resolvedUuid, appliedFilters).pipe(
              map((response: any) => ({
                ...(response || {}),
                requestData: {
                  ...(response?.requestData || {}),
                  entity: response?.requestData?.entity || 'simulation',
                  uuid: response?.requestData?.uuid || resolvedUuid,
                  appliedFilters,
                  priceChanges: refreshedPriceChanges
                }
              }))
            );
          })
        )
        : this.priceFileSimulationService.getDeepDiveDetails(resolvedUuid);

      deepDiveRequest$
        .pipe(takeUntil(this.unSubscribe$))
        .subscribe({
          next: (response: any) => {
            this.isLoading = false;
            this.simulationService.setDeepDiveDetailsResponse(response);
            this.router.navigate([action.route], { queryParams });
          },
          error: (err) => {
            this.isLoading = false;
            const errorMessage = err?.error?.message || err?.message || 'Failed to fetch deep dive details.';
            this.snackBar.open(errorMessage, 'Close', {
              duration: 4000,
              horizontalPosition: 'right',
              verticalPosition: 'bottom',
              panelClass: ['error-snackbar']
            });
          }
        });
      return;
    }

    this.router.navigate([action.route], { queryParams });
  }

  private resolveSimulationUuid(source: any): string {
    const directUuid = String(
      source?.quote_uuid || source?.uuid || source?.simulation_uuid || ''
    ).trim();
    if (directUuid) {
      return directUuid;
    }

    const businessId = String(source?.businessId || source?.business_id || '').trim();
    return this.extractUuidFromBusinessId(businessId);
  }

  private buildActionApiConfig(action: any): any {
    const apiConfig = { ...(action.apiConfig || {}) };
    const passthroughKeys = [
      'skipDefaultPayload',
      'customPayload',
      'functionName',
      'version',
      'entity',
      'embedQueryParams',
      'embeddedParams',
      'useQueryParam',
      'orderBy'
    ];

    for (const key of passthroughKeys) {
      if (action[key] !== undefined && apiConfig[key] === undefined) {
        apiConfig[key] = action[key];
      }
    }

    if (apiConfig.skipDefaultPayload === undefined) {
      apiConfig.skipDefaultPayload = true;
    }

    return apiConfig;
  }

  private buildActionFilters(action: any, selectedRow: any): any {
    const includeSelectedInRequest = action.includeSelectedRowInRequestParams === true;
    const includeSelectedInQuery = action.includeSelectedRowInQueryParams === true;

    return {
      requestParams: {
        ...(this.currentFilters?.requestParams || {}),
        ...(action.requestParams || {}),
        ...(includeSelectedInRequest && selectedRow ? selectedRow : {})
      },
      queryParams: {
        ...(this.currentFilters?.queryParams || {}),
        ...(action.queryParams || {}),
        ...(includeSelectedInQuery && selectedRow ? selectedRow : {})
      }
    };
  }

  private handleGenericApiAction(action: any): void {
    if (!action?.apiUrl) {
      this.snackBar.open(`Missing apiUrl for action "${action?.label || 'Unnamed'}".`, 'Close', {
        duration: 4000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom',
        panelClass: ['error-snackbar']
      });
      return;
    }

    const selectedRow = this.selectedRows.size > 0 ? Array.from(this.selectedRows)[0] : null;
    const actionFilters = this.buildActionFilters(action, selectedRow);
    const actionConfig = this.buildActionApiConfig(action);
    const actionMethod = String(action.method || 'post').toLowerCase();

    this.isLoading = true;
    this.apiConfigService
      .getApi(action.apiUrl, actionFilters, actionConfig, actionMethod)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.snackBar.open(action.successMessage || `${action.label || 'Action'} completed successfully.`, 'Close', {
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'bottom',
            panelClass: ['success-snackbar']
          });

          if (action.refreshOnSuccess) {
            this.loadGenericTableData();
          }

          if (action.navigateAfterApi && action.route) {
            this.navigateWithSelectedRow(action);
          }
        },
        error: (err) => {
          this.isLoading = false;
          const errorMessage = action.errorMessage
            || (err.error && typeof err.error === 'string'
              ? err.error
              : (err.error?.message || err.message || 'Action failed.'));

          this.snackBar.open(errorMessage, 'Close', {
            duration: 4000,
            horizontalPosition: 'right',
            verticalPosition: 'bottom',
            panelClass: ['error-snackbar']
          });
        }
      });
  }

  handleAddFilter() {
    // specific logic for simulation filters
    // Extract Entity, Field, Operator, Value
    const entityRow = this.genericTableData.find(r => r.property === 'Entity');
    const fieldRow = this.genericTableData.find(r => r.property === 'Field');
    const operatorRow = this.genericTableData.find(r => r.property === 'Operator');
    const valueRow = this.genericTableData.find(r => r.property === 'Value');

    if (entityRow && entityRow.value) {
      const rawEntity = String(entityRow.value).trim().toLowerCase();
      const entityName = rawEntity
        .replace(/^\d+\s*[-:]\s*/, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
      this.simulationService.setEntity(entityName);
    }

    if (fieldRow && fieldRow.value && valueRow && valueRow.value) {
      const field = fieldRow.value;
      const operator = operatorRow?.value || 'Equals';
      const allowWhitespaceSplit = valueRow.allowManualEntry === true;
      const values = this.normalizeManualFilterValues(valueRow.value, allowWhitespaceSplit);
      if (values.length === 0) {
        return;
      }

      // Map display label to API symbol and preserve symbol values from dropdown.
      const operatorMap: Record<string, string> = {
        'Equals': '=',
        '=': '=',
        'Not Equals': '!=',
        '!=': '!=',
        'Greater Than': '>',
        '>': '>',
        'Greater Than or Equal': '>=',
        'Greater Than or Equals': '>=',
        '>=': '>=',
        'Less Than': '<',
        '<': '<',
        'Less Than or Equal': '<=',
        'Less Than or Equals': '<=',
        '<=': '<='
      };
      const opSym = operatorMap[operator] || operator || '=';
      const forceArray = this.isListOperator(opSym);

      // Add to structured filters for API (Standard format)
      const filterValue = values.length <= 1 ? (values[0] ?? '') : values;
      this.structuredAppliedFilters.push({
        attributeName: field,
        operator: opSym,
        attributeValues: this.toPayloadFilterValue(filterValue, forceArray || allowWhitespaceSplit, operator),
        value: this.toPayloadFilterValue(filterValue, forceArray || allowWhitespaceSplit, operator),
        multiValueInput: forceArray || allowWhitespaceSplit,
        logicalOperator: 'AND'
      });
      this.syncAppliedFiltersDisplayFromStructured();
      this.persistAppliedFiltersState();

      // Clear only the Field and Value inputs for the next filter
      if (fieldRow) fieldRow.value = '';
      if (valueRow) valueRow.value = '';
    }
  }

  toggleAppliedFiltersEditMode(): void {
    this.appliedFiltersEditMode = !this.appliedFiltersEditMode;
  }

  removeAppliedFilter(index: number): void {
    if (index < 0 || index >= this.appliedFilters.length) {
      return;
    }

    this.appliedFilters = this.appliedFilters.filter((_, idx) => idx !== index);
    this.structuredAppliedFilters = (this.structuredAppliedFilters || [])
      .filter((_, idx) => idx !== index)
      .map((filter, idx, all) => ({
        ...filter,
        logicalOperator: idx === all.length - 1 ? '' : 'AND'
      }));

    this.persistAppliedFiltersState(this.structuredAppliedFilters.length === 0);

    if (this.appliedFilters.length === 0) {
      this.appliedFiltersEditMode = false;
    }
  }

  handleResetGeneric() {
    // Revert to original data (clears edits)
    // Deep copy to reset
    this.genericTableData = JSON.parse(JSON.stringify(this.originalGenericData));
    // Also clear applied filters? User workflow: Reset usually clears the form.
    // To clear applied filters, maybe a separate 'Clear All' button on the list?
    // For now, let's keep applied filters, just reset the inputs.
  }

  handleClearGeneric() {
    // Clear all input values (set to empty string)
    if (this.genericTableData) {
      this.genericTableData.forEach(row => {
        row.value = '';
      });
    }

    if (this.widgetId === 'simulation-filters') {
      this.clearAllAppliedFilters();
    }
  }

  updateSummaryTable(updates: any[]) {
    // Updates is array of { metric, value }
    // If table is empty, just set the data directly
    if (!this.genericTableData || this.genericTableData.length === 0) {
      this.genericTableData = [...updates];
      return;
    }

    updates.forEach(update => {
      const row = this.genericTableData.find(r => r.metric === update.metric);
      if (row) {
        row.value = update.value;
      } else {
        // Add new row if not found
        this.genericTableData.push(update);
      }
    });

    // Re-assign to trigger change detection in Angular
    this.genericTableData = [...this.genericTableData];
  }

  handleSaveSimulation() {
    const dialogRef = this.dialog.open(SaveSimulationDialogComponent, {
      width: '550px',
      panelClass: 'custom-dialog-container',
      data: { title: 'Save Simulation', name: '' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result?.name) {
        return;
      }

      this.isLoading = true;
      const payload = {
        name: result.name,
        description: result.description,
        filters: this.simulationService.getSimulationFilters(),
        values: this.simulationService.getSimulationValues()
      };

      this.priceFileSimulationService
        .saveSimulationQuote(payload)
        .pipe(takeUntil(this.unSubscribe$))
        .subscribe({
          next: (response) => {
            this.isLoading = false;

            const simulationRecord =
              response?.responseList?.[0]?.simulation?.[0] ||
              response?.responseList?.[0]?.sim_quote?.[0] ||
              {};
            const simulationData = simulationRecord?.data || {};
            const operation = String(simulationRecord?.operation || '').trim().toLowerCase();
            const responseMessage = String(simulationRecord?.message || '').trim();
            const isDuplicateName =
              operation === 'non' ||
              responseMessage.toLowerCase().includes('already exists');
            const isInsertSuccess =
              operation === 'insert' ||
              responseMessage.toLowerCase().includes('new record created');

            if (isDuplicateName) {
              this.snackBar.open(responseMessage || 'Simulation name already exists', 'Close', {
                duration: 4000,
                horizontalPosition: 'right',
                verticalPosition: 'bottom',
                panelClass: ['error-snackbar']
              });
              return;
            }

            if (!isInsertSuccess) {
              this.snackBar.open(responseMessage || 'Failed to save simulation. Please try again.', 'Close', {
                duration: 4000,
                horizontalPosition: 'right',
                verticalPosition: 'bottom',
                panelClass: ['error-snackbar']
              });
              return;
            }

            const savedSimulationName = simulationData.name ?? result.name;
            // Try to get UUID from: outer record level → data → extract from businessId
            const rawBusinessId = response?.businessId || '';
            const recordUuid =
              simulationRecord?.uuid ||
              simulationData.uuid ||
              this.extractUuidFromBusinessId(rawBusinessId);
            console.log('[SimulationInsert] commitStatus:', response?.commitStatus, 'recordUuid:', recordUuid, 'businessId:', rawBusinessId);
            const newRow: any = {
              s_no: (this.genericTableData?.length || 0) + 1,
              name: savedSimulationName,
              description: simulationData.description ?? '',
              created_date: this.formatDateOnly(simulationData.created_date),
              last_execution_date: this.formatDateOnly(simulationData.last_execution_date),
              current_sales: simulationData.current_sales ?? '',
              current_units: simulationData.current_units ?? simulationData.current_annual_units ?? '',
              impact_dollar: simulationData.impact_dollar ?? '',
              impact_percent: simulationData.impact_percent ?? simulationData.impact_percentage ?? '',
              status: simulationData.status ?? 'Active',
              uuid: recordUuid,
              businessId: rawBusinessId
            };

            // Always add to persistent memory service
            this.simulationService.addNewSimulation(newRow);

            const finishSaveFlow = () => {
              this.snackBar.open(responseMessage || `Simulation "${savedSimulationName}" saved successfully!`, 'Close', {
                duration: 3000,
                horizontalPosition: 'right',
                verticalPosition: 'bottom',
                panelClass: ['success-snackbar']
              });
            };

            // Always refresh simulation list after save so table uses latest backend UUID/state.
            this.priceFileSimulationService
              .getSimulationList()
              .pipe(takeUntil(this.unSubscribe$))
              .subscribe({
                next: (sims: any[]) => {
                  const match = sims.find((s: any) =>
                    (recordUuid && s?.uuid === recordUuid) ||
                    String(s?.name || '').trim().toLowerCase() === String(savedSimulationName || '').trim().toLowerCase()
                  );
                  const realUuid = match?.quote_uuid || match?.uuid || match?.id;
                  if (match) {
                    console.log('[SimulationInsert] Refreshed UUID from backend list:', realUuid, 'for name:', match.name);
                    newRow.uuid = realUuid || newRow.uuid;
                    newRow.s_no = match.s_no || match.id || newRow.s_no;
                    newRow.name = match.name ?? newRow.name;
                    newRow.description = match.description ?? newRow.description;
                    newRow.created_date = this.formatDateOnly(match.created_date ?? newRow.created_date);
                    newRow.last_execution_date = this.formatDateOnly(match.last_execution_date ?? newRow.last_execution_date);
                    newRow.current_sales = match.current_sales ?? newRow.current_sales;
                    newRow.current_units = match.current_units ?? match.current_annual_units ?? newRow.current_units;
                    newRow.impact_dollar = match.impact_dollar ?? newRow.impact_dollar;
                    newRow.impact_percent = match.impact_percent ?? match.impact_percentage ?? newRow.impact_percent;
                    newRow.status = match.status ?? newRow.status;
                  }

                  this.simulationService.updateSimulationByName(savedSimulationName, {
                    ...newRow
                  });

                  if (this.widgetId === 'simulation-table') {
                    this.loadGenericTableData();
                  }

                  finishSaveFlow();
                },
                error: (err) => {
                  console.warn('[SimQuoteInsert] Could not refresh simulation list after save', err);
                  if (this.widgetId === 'simulation-table') {
                    this.loadGenericTableData();
                  }
                  finishSaveFlow();
                }
              });
          },
          error: (err) => {
            console.error('Failed to save simulation', err);
            this.isLoading = false;
            this.snackBar.open('Failed to save simulation. Please try again.', 'Close', {
              duration: 4000,
              horizontalPosition: 'right',
              verticalPosition: 'bottom',
              panelClass: ['error-snackbar']
            });
          }
        });
    });
  }

  private loadSimulationFiltersTable(): void {
    this.genericLoading = true;
    const selectedUuid = this.getSelectedSimulationUuid();
    const rows: SimulationDropdownRow[] = [
      { property: 'Entity', value: '', allowManualEntry: false },
      { property: 'Field', value: '', allowManualEntry: false },
      { property: 'Operator', value: '', allowManualEntry: false },
      { property: 'Value', value: '', allowManualEntry: false }
    ];

    forkJoin({
      entities: this.priceFileSimulationService.getEntityOptions(selectedUuid),
      operators: this.priceFileSimulationService.getOperatorOptions(selectedUuid)
    })
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: ({ entities, operators }) => {
          rows[0].value_options = this.normalizeEntityOptions(entities);
          rows[2].value_options = operators;

          if (rows[0].value_options?.length) {
            rows[0].value = rows[0].value || rows[0].value_options[0];
          }
          if (operators?.length) {
            rows[2].value = rows[2].value || operators[0];
          }

          this.genericTableData = rows;
          this.simulationFilterValueRow = rows[3];
          this.genericLoading = false;
          this.originalGenericData = [...rows];
          this.syncSimulationData();
          this.restoreAppliedFiltersStateFromStorage();
          this.hydrateAppliedFiltersFromSimulationState();
          if (selectedUuid) {
            this.loadSelectedSimulationDetails();
          } else {
            this.loadFieldOptionsForSelectedEntity(rows[0].value);
          }
        },
        error: (err) => {
          this.genericLoading = false;
          console.error('Failed to load simulation filter dropdowns', err);
          this.snackBar.open('Unable to load filter dropdowns.', 'Close', {
            duration: 4000,
            horizontalPosition: 'right',
            verticalPosition: 'bottom',
            panelClass: ['error-snackbar']
          });
        }
      });
  }

  private hydrateAppliedFiltersFromSimulationState(): void {
    if (this.widgetId !== 'simulation-filters') {
      return;
    }

    // Keep existing rendered list if already present.
    if (Array.isArray(this.appliedFilters) && this.appliedFilters.length > 0) {
      return;
    }

    const sharedFilters = Array.isArray(this.simulationService.getAppliedFilters())
      ? this.simulationService.getAppliedFilters()
      : [];
    if (sharedFilters.length === 0) {
      return;
    }

    this.structuredAppliedFilters = sharedFilters.map((filter: any, index: number, all: any[]) => ({
      ...filter,
      logicalOperator: this.resolveLogicalOperator(filter?.logicalOperator, index, all.length)
    }));

    this.syncAppliedFiltersDisplayFromStructured();
    this.persistAppliedFiltersState();
  }

  clearAllAppliedFilters(): void {
    this.appliedFilters = [];
    this.structuredAppliedFilters = [];
    this.appliedFilterDialogState = null;
    this.appliedFiltersEditMode = false;
    if (typeof localStorage !== 'undefined') {
      const activeUuid = String(this.getSelectedSimulationUuid() || this.simulationService.getLastRunUuid() || '').trim();
      if (activeUuid) {
        localStorage.removeItem(this.getAppliedFilterDialogStateStorageKeyByUuid(activeUuid));
      }
    }
    this.persistAppliedFiltersState(true);
  }

  private normalizeEntityOptions(rawEntities: any): string[] {
    const fallbackEntities = ['030 - DN Pricing', '035 - Base Price'];
    const entities = Array.isArray(rawEntities) ? rawEntities : [];

    const normalized = entities
      .map((entity: any) => String(entity ?? '').trim())
      .filter((entity: string) => entity !== '')
      .map((entity: string) => {
        const token = entity.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
        if (token === 'dn_pricing' || token === '030_dn_pricing') {
          return '030 - DN Pricing';
        }
        if (token === 'dn_price' || token === '030_dn_price') {
          return '030 - DN Pricing';
        }
        if (token === 'base_price' || token === '035_base_price') {
          return '035 - Base Price';
        }
        return entity;
      });

    const merged = Array.from(new Set([...normalized, ...fallbackEntities]));
    const preferredOrder = ['030 - DN Pricing', '035 - Base Price'];
    return merged.sort((a, b) => {
      const aIdx = preferredOrder.indexOf(a);
      const bIdx = preferredOrder.indexOf(b);
      if (aIdx !== -1 || bIdx !== -1) {
        if (aIdx === -1) return 1;
        if (bIdx === -1) return -1;
        return aIdx - bIdx;
      }
      return a.localeCompare(b);
    });
  }

  private hasTransientSimulationUuid(uuid: any): boolean {
    const normalizedUuid = String(uuid || '').trim();
    if (!normalizedUuid) {
      return true;
    }
    return normalizedUuid.includes('-sim_quote-') || normalizedUuid.includes('-simulation-');
  }

  private loadFieldOptionsForSelectedEntity(entityValue: string): void {
    const selectedUuid = this.getSelectedSimulationUuid();
    const fieldRow = this.genericTableData.find(r => r.property === 'Field');
    if (!fieldRow) {
      return;
    }

    fieldRow['value_options'] = [];
    fieldRow.value = '';

    if (this.simulationFilterValueRow) {
      this.simulationFilterValueRow.allowManualEntry = false;
      this.simulationFilterValueRow['value_options'] = [];
      this.simulationFilterValueRow.value = '';
    }

    this.priceFileSimulationService
      .getFieldOptions(entityValue, selectedUuid)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (fields) => {
          fieldRow['value_options'] = fields;
          if (fields?.length) {
            fieldRow.value = fields[0];
            this.updateFilterValueEntryMode(fieldRow.value);
          }
          this.syncSimulationData();
        },
        error: (err) => {
          console.error('Failed to load field options', err);
          fieldRow['value_options'] = [];
          this.syncSimulationData();
        }
      });
  }

  private loadSimulationValuesTable(): void {
    this.genericLoading = true;
    const selectedUuid = this.getSelectedSimulationUuid();
    const rows: SimulationDropdownRow[] = [
      { property: 'Entity', value: '', allowManualEntry: false },
      { property: 'Price Change Type', value: '', allowManualEntry: false },
      { property: 'Price Change Value', value: '', allowManualEntry: true },
      { property: 'Based on', value: '', allowManualEntry: false }
    ];

    forkJoin({
      entities: this.priceFileSimulationService.getEntityOptions(selectedUuid),
      types: this.priceFileSimulationService.getPriceChangeTypeOptions(selectedUuid),
      basedOn: this.priceFileSimulationService.getBasedOnOptions(selectedUuid)
    })
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: ({ entities, types, basedOn }) => {
          rows[0].value_options = this.normalizeEntityOptions(entities);
          rows[1].value_options = types;
          rows[3].value_options = basedOn;

          const currentEntity = this.simulationService.getEntity();
          rows[0].value = this.matchOptionValue(rows[0].value_options, currentEntity || rows[0].value || '');
          if (!rows[0].value && rows[0].value_options?.length) {
            rows[0].value = rows[0].value_options[0];
          }
          if (rows[0].value) {
            this.simulationService.setEntity(rows[0].value);
          }

          if (types?.length) {
            rows[1].value = rows[1].value || types[0];
          }
          if (basedOn?.length) {
            rows[3].value = rows[3].value || basedOn[0];
          }

          this.genericTableData = rows;
          this.simulationPriceChangeValueRow = rows[2];
          this.genericLoading = false;
          this.originalGenericData = [...rows];
          this.syncSimulationData();

          this.loadPriceChangeValueOptions(rows[1].value || 'value');
          this.loadSelectedSimulationDetails();
        },
        error: (err) => {
          this.genericLoading = false;
          console.error('Failed to load simulation value dropdowns', err);
          this.snackBar.open('Unable to load simulation values.', 'Close', {
            duration: 4000,
            horizontalPosition: 'right',
            verticalPosition: 'bottom',
            panelClass: ['error-snackbar']
          });
        }
      });
  }

  onDropdownValueChange(row: any, columnKey: string, value: any): void {
    row[columnKey] = value;

    if (this.widgetId === 'simulation-filters' && row.property === 'Entity') {
      this.loadFieldOptionsForSelectedEntity(value);
      this.syncSimulationData();
      return;
    }

    if (this.widgetId === 'simulation-filters' && row.property === 'Field') {
      this.updateFilterValueEntryMode(value);
    }

    if (this.widgetId === 'simulation-values' && row.property === 'Price Change Type') {
      this.loadPriceChangeValueOptions(value);
    }
    if (this.widgetId === 'simulation-values' && row.property === 'Entity') {
      this.simulationService.setEntity(value);
    }
    this.syncSimulationData();
  }

  onGenericTextValueChange(row: any, columnKey: string, value: any): void {
    row[columnKey] = value;
    this.syncSimulationData();
  }

  private syncSimulationData() {
    if (this.widgetId === 'simulation-filters') {
      this.simulationService.setSimulationFilters(this.genericTableData);
    } else if (this.widgetId === 'simulation-values') {
      this.simulationService.setSimulationValues(this.genericTableData);
    }
  }

  private loadFilterValueOptions(field: string): void {
    if (!this.simulationFilterValueRow) {
      return;
    }
    this.simulationFilterValueRow['value_options'] = [];
    if (!field) {
      return;
    }
    const entityRow = this.genericTableData.find(r => r.property === 'Entity');
    const selectedEntity = entityRow?.value || '';
    const selectedUuid = this.getSelectedSimulationUuid();
    this.priceFileSimulationService
      .getValueOptions(field, selectedEntity, selectedUuid)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (options) => {
          this.simulationFilterValueRow['value_options'] = options;
        },
        error: (err) => {
          console.error('Failed to load value options', err);
          this.simulationFilterValueRow['value_options'] = [];
        }
      });
  }

  private loadPriceChangeValueOptions(type: string): void {
    if (!this.simulationPriceChangeValueRow) {
      return;
    }
    this.simulationPriceChangeValueRow['value_options'] = [];
  }

  getDropdownOptions(row: any, col: any): string[] {
    const options = row?.[`${col.key}_options`] || col?.options || [];
    return Array.isArray(options) ? options : [];
  }

  getSimulationValuesEntityRow(): any | null {
    if (this.widgetId !== 'simulation-values' || !Array.isArray(this.genericTableData)) {
      return null;
    }
    return this.genericTableData.find((row: any) => String(row?.property || '').trim().toLowerCase() === 'entity') || null;
  }

  getVisibleGenericRows(): any[] {
    if (!Array.isArray(this.genericTableData)) {
      return [];
    }

    if (this.widgetId !== 'simulation-values') {
      return this.genericTableData;
    }

    return this.genericTableData.filter(
      (row: any) => String(row?.property || '').trim().toLowerCase() !== 'entity'
    );
  }

  private isSimulationFiltersFieldDropdown(row: any, col: any): boolean {
    return this.widgetId === 'simulation-filters'
      && String(row?.property ?? '').trim().toLowerCase() === 'field'
      && String(col?.key ?? '').trim().toLowerCase() === 'value';
  }

  getDropdownOptionLabel(row: any, col: any, option: any): string {
    const rawOption = String(option ?? '');
    if (!this.isSimulationFiltersFieldDropdown(row, col)) {
      return rawOption;
    }

    return this.simulationFiltersFieldLabelMap[rawOption] || rawOption;
  }

  getFilteredDropdownOptions(row: any, col: any): string[] {
    const options = this.getDropdownOptions(row, col);
    const searchTerm = this.getDropdownSearchTerm(row, col?.key).trim().toLowerCase();
    if (!searchTerm) {
      return options;
    }

    return options.filter((option: any) => {
      const rawOption = String(option ?? '');
      if (this.isSimulationFiltersFieldDropdown(row, col)) {
        const displayLabel = this.getDropdownOptionLabel(row, col, rawOption).toLowerCase();
        return rawOption.toLowerCase().includes(searchTerm) || displayLabel.includes(searchTerm);
      }

      return rawOption.toLowerCase().includes(searchTerm);
    });
  }

  onDropdownSearchChange(row: any, columnKey: string, value: string): void {
    const key = this.getDropdownSearchKey(row, columnKey);
    this.dropdownSearchText[key] = String(value ?? '');
  }

  onDropdownOpenedChange(row: any, columnKey: string, opened: boolean, inputEl?: HTMLInputElement): void {
    if (opened) {
      if (inputEl) {
        setTimeout(() => {
          inputEl.focus();
        }, 100);
      }
      return;
    }
    const key = this.getDropdownSearchKey(row, columnKey);
    delete this.dropdownSearchText[key];
  }

  getDropdownSearchTerm(row: any, columnKey: string): string {
    const key = this.getDropdownSearchKey(row, columnKey);
    return this.dropdownSearchText[key] || '';
  }

  private getDropdownSearchKey(row: any, columnKey: string): string {
    const rowKey = String(row?.property ?? '');
    return `${this.widgetId || 'editable-table'}::${rowKey}::${columnKey}`;
  }

  getConfiguredTableHeight(): string | null {
    const rawHeight = this.data?.height ?? this.data?.tableHeight ?? this.data?.tableMaxHeight ?? this.data?.maxHeight;

    if (rawHeight === null || rawHeight === undefined || rawHeight === '') {
      return null;
    }

    if (typeof rawHeight === 'number') {
      return `${rawHeight}px`;
    }

    const normalizedHeight = String(rawHeight).trim();
    if (/^\d+(\.\d+)?$/.test(normalizedHeight)) {
      return `${normalizedHeight}px`;
    }

    return normalizedHeight;
  }

  getTopActionsContainerStyle(): { [key: string]: string } {
    const topActionsSpacing = this.data?.topActionsSpacing || {};
    const actionsSpacing = this.data?.actionsSpacing || {};
    return this.buildMarginStyle(
      topActionsSpacing.marginTop ?? this.data?.topActionsMarginTop ?? actionsSpacing.marginTop ?? this.data?.actionsMarginTop,
      topActionsSpacing.marginBottom ?? this.data?.topActionsMarginBottom ?? actionsSpacing.marginBottom ?? this.data?.actionsMarginBottom ?? 10
    );
  }

  getBottomActionsContainerStyle(): { [key: string]: string } {
    const bottomActionsSpacing = this.data?.bottomActionsSpacing || {};
    const actionsSpacing = this.data?.actionsSpacing || {};
    return this.buildMarginStyle(
      bottomActionsSpacing.marginTop ?? this.data?.bottomActionsMarginTop ?? actionsSpacing.marginTop ?? this.data?.actionsMarginTop ?? 10,
      bottomActionsSpacing.marginBottom ?? this.data?.bottomActionsMarginBottom ?? actionsSpacing.marginBottom ?? this.data?.actionsMarginBottom
    );
  }

  getTableContainerStyle(): { [key: string]: string } {
    const tableSpacing = this.data?.tableSpacing || {};
    const configuredMarginTop = tableSpacing.marginTop ?? this.data?.tableMarginTop;
    const shouldApplyTopMargin = this.getTopActions().length > 0 || this.data?.alwaysApplyTableMarginTop === true;

    return this.buildMarginStyle(
      shouldApplyTopMargin ? configuredMarginTop : null,
      tableSpacing.marginBottom ?? this.data?.tableMarginBottom
    );
  }

  private buildMarginStyle(marginTop: any, marginBottom: any): { [key: string]: string } {
    const styles: { [key: string]: string } = {};
    const normalizedTop = this.normalizeCssLength(marginTop);
    const normalizedBottom = this.normalizeCssLength(marginBottom);

    if (normalizedTop) {
      styles['margin-top'] = normalizedTop;
    }
    if (normalizedBottom) {
      styles['margin-bottom'] = normalizedBottom;
    }

    return styles;
  }

  private normalizeCssLength(rawValue: any): string | null {
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return null;
    }

    if (typeof rawValue === 'number' && Number.isFinite(rawValue)) {
      return `${rawValue}px`;
    }

    const normalizedValue = String(rawValue).trim();
    if (!normalizedValue) {
      return null;
    }

    if (/^\d+(\.\d+)?$/.test(normalizedValue)) {
      return `${normalizedValue}px`;
    }

    return normalizedValue;
  }

  handleRunSimulation() {
    if (!this.hasAnyAppliedFilters()) {
      this.snackBar.open('Please apply at least one filter before running simulation.', 'Close', {
        duration: 3500,
        horizontalPosition: 'right',
        verticalPosition: 'bottom',
        panelClass: ['error-snackbar']
      });
      return;
    }

    if (this.widgetId === 'simulation-values' && !this.isPriceChangeValueNumeric()) {
      this.snackBar.open('Run is enabled only when Price Change Value is a valid number.', 'Close', {
        duration: 3500,
        horizontalPosition: 'right',
        verticalPosition: 'bottom',
        panelClass: ['error-snackbar']
      });
      return;
    }

    this.isLoading = true;
    this.enableGlobalRunFreeze();
    const selectedUuid = this.getSelectedSimulationUuid();
    this.logRunSaveAppliedFilters('EditableTable.handleRunSimulation.beforeServiceRun', {
      widgetId: this.widgetId,
      selectedUuid,
      genericTableData: this.genericTableData,
      simulationServiceCurrentAppliedFilters: this.simulationService.getAppliedFilters(),
      simulationServiceLastRunUuid: this.simulationService.getLastRunUuid(),
      simulationServiceLastRunAppliedFilters: this.simulationService.getLastRunAppliedFilters(),
      structuredAppliedFilters: this.structuredAppliedFilters,
      displayAppliedFilters: this.appliedFilters
    });
    this.simulationService.runSimulation(this.genericTableData, selectedUuid).subscribe({
      next: (res) => {
        this.isLoading = false;
        this.disableGlobalRunFreeze();
        this.logRunSaveAppliedFilters('EditableTable.handleRunSimulation.afterServiceRun', {
          response: res,
          simulationServiceLastRunUuid: this.simulationService.getLastRunUuid(),
          simulationServiceLastRunAppliedFilters: this.simulationService.getLastRunAppliedFilters()
        });
        console.log('run response ravi', res);
        if (res.success) {
          this.snackBar.open('Simulation Run Successfully!', 'Close', {
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'bottom',
            panelClass: ['success-snackbar']
          });

        } else {
          this.snackBar.open(`Simulation failed: ${res.error || 'Unknown error'}`, 'Close', {
            duration: 5000,
            horizontalPosition: 'right',
            verticalPosition: 'bottom',
            panelClass: ['error-snackbar']
          });
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.disableGlobalRunFreeze();
        this.snackBar.open('API call failed. Please check your connection.', 'Close', {
          duration: 4000,
          horizontalPosition: 'right',
          verticalPosition: 'bottom',
          panelClass: ['error-snackbar']
        });
      }
    });
  }

  private enableGlobalRunFreeze(): void {
    EditableTableComponent.globalRunFreezeCount += 1;
    if (EditableTableComponent.globalRunFreezeOverlay) {
      return;
    }

    const overlay = document.createElement('div');
    overlay.setAttribute('id', 'global-run-freeze-overlay');
    overlay.style.position = 'fixed';
    overlay.style.inset = '0';
    overlay.style.zIndex = '2147483647';
    overlay.style.background = 'rgba(255, 255, 255, 0.72)';
    overlay.style.display = 'flex';
    overlay.style.flexDirection = 'column';
    overlay.style.alignItems = 'center';
    overlay.style.justifyContent = 'center';
    overlay.style.gap = '12px';
    overlay.style.pointerEvents = 'all';
    overlay.innerHTML = `
      <div style="width:42px;height:42px;border:4px solid #bfdbfe;border-top-color:#0b63c6;border-radius:50%;animation: run-freeze-spin 1s linear infinite;"></div>
      <span style="font-size:14px;font-weight:600;color:#1f2937;">Processing...</span>
    `;

    if (!document.getElementById('global-run-freeze-spin-style')) {
      const style = document.createElement('style');
      style.id = 'global-run-freeze-spin-style';
      style.textContent = '@keyframes run-freeze-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }';
      document.head.appendChild(style);
    }

    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';
    EditableTableComponent.globalRunFreezeOverlay = overlay;
  }

  private disableGlobalRunFreeze(): void {
    EditableTableComponent.globalRunFreezeCount = Math.max(0, EditableTableComponent.globalRunFreezeCount - 1);
    if (EditableTableComponent.globalRunFreezeCount > 0) {
      return;
    }

    if (EditableTableComponent.globalRunFreezeOverlay) {
      EditableTableComponent.globalRunFreezeOverlay.remove();
      EditableTableComponent.globalRunFreezeOverlay = null;
    }
    document.body.style.overflow = '';
  }

  onRowEdit(row: any): void {
    if (this.data?.rowEditAction === 'open-dialog') {
      this.handleEditSimulation(row);
      return;
    }
    if (this.data?.rowEditRoute) {
      // Navigate to the configure route with the row data as query params
      // We explicitly include 'isEditMode' or similar if needed, or just pass the data
      this.router.navigate([this.data.rowEditRoute], { queryParams: row });
      return;
    }
    // Enable inline editing
    row.isEditing = true;
    row.originalData = { ...row }; // Backup for cancel
  }

  handleEditSimulation(row: any) {
    const dialogRef = this.dialog.open(SaveSimulationDialogComponent, {
      width: '550px',
      panelClass: 'custom-dialog-container',
      data: {
        title: 'Edit Simulation',
        name: row.name || '',
        description: row.description || ''
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result?.name) {
        return;
      }

      this.isLoading = true;

      // Call the API with uuid to update the record in the database
      this.priceFileSimulationService
        .saveSimulationQuote({
          name: result.name,
          description: result.description || '',
          uuid: row.uuid || ''
        })
        .pipe(takeUntil(this.unSubscribe$))
        .subscribe({
          next: (response) => {
            this.isLoading = false;

            const simulationRecord =
              response?.responseList?.[0]?.simulation?.[0] ||
              response?.responseList?.[0]?.sim_quote?.[0] ||
              {};
            const updatedData = simulationRecord?.data || {};
            const responseMessage = String(simulationRecord?.message || '').trim();
            const operation = String(simulationRecord?.operation || '').trim().toLowerCase();
            const isDuplicateName =
              operation === 'non' ||
              responseMessage.toLowerCase().includes('already exists');

            if (isDuplicateName) {
              this.snackBar.open(responseMessage || 'Simulation name already exists', 'Close', {
                duration: 4000,
                horizontalPosition: 'right',
                verticalPosition: 'bottom',
                panelClass: ['error-snackbar']
              });
              return;
            }

            // Update local row data from the API response
            row.name = updatedData?.name ?? row.name;
            row.description = updatedData?.description ?? row.description;
            if (updatedData?.uuid) {
              row.uuid = updatedData.uuid;
            }

            // Update original data source
            const index = this.originalGenericData.indexOf(row);
            if (index > -1) {
              this.originalGenericData[index] = { ...row };
            }

            this.snackBar.open(responseMessage || `Simulation "${row.name}" updated successfully!`, 'Close', {
              duration: 3000,
              horizontalPosition: 'right',
              verticalPosition: 'bottom',
              panelClass: ['success-snackbar']
            });
          },
          error: (err) => {
            console.error('Failed to update simulation', err);
            this.isLoading = false;
            this.snackBar.open('Failed to update simulation. Please try again.', 'Close', {
              duration: 4000,
              horizontalPosition: 'right',
              verticalPosition: 'bottom',
              panelClass: ['error-snackbar']
            });
          }
        });
    });
  }

  onRowSave(row: any): void {
    row.isEditing = false;
    delete row.originalData;
    // Here you would typically trigger an API call or emit an event
    console.log('Row Saved:', row);

    // Optional: Show success message
    this.snackBar.open('Row updated successfully', 'Close', {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'bottom',
      panelClass: ['success-snackbar']
    });
  }

  onRowCancel(row: any): void {
    // Revert changes
    if (row.originalData) {
      Object.assign(row, row.originalData);
      delete row.originalData;
    }
    row.isEditing = false;
  }

  // --- Selection & Aggregation Mathods ---

  isAllSelected(): boolean {
    return this.genericTableData.length > 0 && this.selectedRows.size === this.genericTableData.length;
  }

  toggleSelectAll(event: any): void {
    if (event.target.checked) {
      this.genericTableData.forEach(row => this.selectedRows.add(row));
    } else {
      this.selectedRows.clear();
    }
    this.calculateTotals();
  }

  toggleSelection(row: any): void {
    if (this.selectedRows.has(row)) {
      this.selectedRows.delete(row);
    } else {
      this.selectedRows.add(row);
    }
    this.calculateTotals();
  }

  deleteSelectedRows(): void {
    if (this.selectedRows.size === 0) {
      return;
    }

    const selectedRows = Array.from(this.selectedRows);

    if (this.widgetId !== 'simulation-table') {
      const toDelete = new Set(selectedRows);
      const deletedCount = toDelete.size;

      this.genericTableData = this.genericTableData.filter(row => !toDelete.has(row));
      this.originalGenericData = this.originalGenericData.filter(row => !toDelete.has(row));
      this.selectedRows.clear();
      this.calculateTotals();

      this.snackBar.open(`Deleted ${deletedCount} ${deletedCount === 1 ? 'row' : 'rows'}.`, 'Close', {
        duration: 3000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom'
      });
      return;
    }

    const rowsWithUuid = selectedRows
      .map((row) => ({ row, uuid: this.resolveSimulationUuid(row) }))
      .filter((item) => item.uuid);
    const missingUuidCount = selectedRows.length - rowsWithUuid.length;

    if (rowsWithUuid.length === 0) {
      this.snackBar.open('Selected simulation UUID is missing.', 'Close', {
        duration: 3500,
        horizontalPosition: 'right',
        verticalPosition: 'bottom',
        panelClass: ['error-snackbar']
      });
      return;
    }

    this.isLoading = true;
    const deleteRequests = rowsWithUuid.map((item) =>
      this.priceFileSimulationService.deleteSimulationByUuid(item.uuid)
    );

    forkJoin(deleteRequests)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: () => {
          this.isLoading = false;

          const toDelete = new Set(rowsWithUuid.map((item) => item.row));
          const deletedCount = toDelete.size;

          this.genericTableData = this.genericTableData.filter((row) => !toDelete.has(row));
          this.originalGenericData = this.originalGenericData.filter((row) => !toDelete.has(row));
          this.selectedRows.clear();
          this.calculateTotals();

          const successMessage = missingUuidCount > 0
            ? `Deleted ${deletedCount} row(s). Skipped ${missingUuidCount} row(s) without UUID.`
            : `Deleted ${deletedCount} ${deletedCount === 1 ? 'row' : 'rows'}.`;

          this.snackBar.open(successMessage, 'Close', {
            duration: 3500,
            horizontalPosition: 'right',
            verticalPosition: 'bottom',
            panelClass: ['success-snackbar']
          });

          this.loadGenericTableData();
        },
        error: (err) => {
          this.isLoading = false;
          const errorMessage = err?.error?.message || err?.message || 'Failed to delete selected simulations.';
          this.snackBar.open(errorMessage, 'Close', {
            duration: 4000,
            horizontalPosition: 'right',
            verticalPosition: 'bottom',
            panelClass: ['error-snackbar']
          });
        }
      });
  }

  calculateTotals(): void {
    if (this.selectedRows.size === 0) {
      this.headerTotals = {};
      return;
    }

    const totals: { [key: string]: number } = {};
    const count: { [key: string]: number } = {};

    this.selectedRows.forEach(row => {
      this.genericColumns.forEach((col: any) => {
        if (!col.showTotal) return; // Skip if aggregation not enabled

        // Simple heuristic: if it looks like a number, sum it.
        // Or check col.type if available (assuming 'number' or 'currency' or 'percentage')
        const valStr = String(row[col.key] || '').replace(/[^0-9.-]+/g, '');
        const val = parseFloat(valStr);

        if (!isNaN(val)) {
          totals[col.key] = (totals[col.key] || 0) + val;
          count[col.key] = (count[col.key] || 0) + 1;
        }
      });
    });

    // If ONLY ONE row is selected, populate ALL columns with that row's data (unless it's a total)
    if (this.selectedRows.size === 1) {
      const singleRow = this.selectedRows.values().next().value;
      this.genericColumns.forEach(col => {
        // If it's a total column, we let the total logic above overwrite/handle it (or we pre-fill it here?)
        // Actually, the total logic below iterates 'totals' which is populated only if showTotal=true.
        // So for !showTotal columns, 'totals[col.key]' is undefined.
        // We can set those here.
        if (!col.showTotal && col.key === 'name') {
          // Use the raw value or formatted? Raw value for now. 
          // If it matches the filter input, it might look like a filter.
          // But user wants to see the "Selected Name".
          this.headerTotals[col.key] = singleRow[col.key];
        }
      });
    }

    this.genericColumns.forEach(col => {
      if (totals[col.key] !== undefined) {
        // Format based on column type or heuristic
        // If original string had '$', add it back. If '%', calc average?
        // For now, simple sum and currency formatting if it looks like money
        const firstRowVal = Array.from(this.selectedRows)[0][col.key];
        const displayContext = `${col.key || ''} ${col.label || ''}`;
        if (String(firstRowVal).includes('$')) {
          this.headerTotals[col.key] = formatValueInMillions(totals[col.key], { preserveCurrency: true });
        } else if (String(firstRowVal).includes('%')) {
          // logic: If %, do Average. Else, do Sum.
          const avg = totals[col.key] / count[col.key];
          this.headerTotals[col.key] = avg.toFixed(2) + '%';
        } else if (/sales|impact|value|revenue|dollar|amount|price|cost|margin|units|volume/i.test(displayContext)) {
          this.headerTotals[col.key] = formatValueInMillions(totals[col.key]);
        } else {
          this.headerTotals[col.key] = this.formatDecimalWithCommas(totals[col.key]);
        }
      }
    });

    // If multiple rows selected, we only show totals. Non-total columns should remain empty or show "Multip..."? 
    // Current logic: headerTotals is initialized to {}, then populated by totals. 
    // If > 1 row, !showTotal columns are undefined in headerTotals. Correct.
  }

  getHeaderValue(colKey: string): string {
    if (this.selectedRows.size > 0 && this.headerTotals[colKey]) {
      return this.headerTotals[colKey];
    }
    return this.filterValues[colKey] || '';
  }

  // --- Excel-Style Filter Methods ---

  extractUniqueValues(): void {
    this.genericColumns.forEach(col => {
      const values = this.originalGenericData.map(row => row[col.key]).filter(v => v !== null && v !== undefined);
      this.uniqueColumnValues[col.key] = Array.from(new Set(values)).sort();
      this.uniqueColumnValues[col.key] = Array.from(new Set(values)).sort();
      // Do NOT initialize selectedFilterValues[col.key] here. 
      // Undefined means "No active filter" (Show All).
    });
  }

  getFilteredOptions(colKey: string): any[] {
    const term = (this.filterSearchText[colKey] || '').toLowerCase();
    const allValues = this.uniqueColumnValues[colKey] || [];
    if (!term) return allValues;
    return allValues.filter(v => String(v).toLowerCase().includes(term));
  }

  isFilterSelected(colKey: string, value: any): boolean {
    // User Request: Default should be UNSELECTED in UI.
    // So if undefined, return false (Unchecked).
    return this.selectedFilterValues[colKey]?.has(value) || false;
  }

  isAllFilterSelected(colKey: string): boolean {
    const filteredOptions = this.getFilteredOptions(colKey);
    if (filteredOptions.length === 0) return false;

    // If undefined, it means "No active filter" -> UI shows "Select All" unchecked?
    // Actually, "Select All" usually reflects if all visual items are selected.
    // If we start unselected, this should be false.
    if (!this.selectedFilterValues[colKey]) {
      return false;
    }

    return filteredOptions.every(v => this.selectedFilterValues[colKey]?.has(v));
  }

  toggleFilterValue(colKey: string, value: any): void {
    if (!this.selectedFilterValues[colKey]) {
      // First interaction: Start with EMPTY set (Additive filtering)
      this.selectedFilterValues[colKey] = new Set();
    }

    if (this.selectedFilterValues[colKey].has(value)) {
      this.selectedFilterValues[colKey].delete(value);
    } else {
      this.selectedFilterValues[colKey].add(value);
    }
  }

  toggleFilterSelectAll(colKey: string, event: any): void {
    const isChecked = event.target.checked;
    const visibleOptions = this.getFilteredOptions(colKey);

    if (!this.selectedFilterValues[colKey]) {
      this.selectedFilterValues[colKey] = new Set();
    }

    if (isChecked) {
      visibleOptions.forEach(v => this.selectedFilterValues[colKey].add(v));
    } else {
      visibleOptions.forEach(v => this.selectedFilterValues[colKey].delete(v));
    }
  }

  onFilterSearch(colKey: string, event: any): void {
    this.filterSearchText[colKey] = event.target.value;
  }

  applyColumnFilter(colKey: string): void {
    // Filter logic:
    // Row is visible if for EVERY column with active filters, the row value is in the set.
    // Exception: If a column has NO selected values in set? 
    // UX Choice: 
    // 1. If set is empty, show nothing? (Strict) -> Default for "uncheck all"
    // 2. If set is empty, show all? -> No, "uncheck all" means hide all.
    // So, strict check.
    // But we need to handle "Initial State" where we want to show all.
    // Let's say: If uniqueColumnValues exists, and selectedFilterValues[col] is undefined, we assume "All".
    // But we initialized sets to empty (for UI). 
    // BETTER: Initialize sets to ALL values.

    this.filterDataLocally();
    // Close menu handled by UI trigger
  }

  clearColumnFilter(colKey: string): void {
    // Reset this column's filter to ALL
    if (this.uniqueColumnValues[colKey]) {
      this.selectedFilterValues[colKey] = new Set();
      delete this.selectedFilterValues[colKey];
    }
    this.filterValues[colKey] = ''; // Clear text input
    this.filterDataLocally();
    this.filterSearchText[colKey] = '';
  }

  filterDataLocally(): void {
    this.genericTableData = this.originalGenericData.filter(row => {
      // 1. Check Text Filters (Header Inputs)
      const textMatch = this.genericColumns.every(col => {
        const filterText = this.filterValues[col.key];
        if (!filterText) return true;
        const cellValue = String(row[col.key] || '').toLowerCase();
        return cellValue.includes(filterText);
      });
      if (!textMatch) return false;

      // 2. Check Excel Filters (Menu Checkboxes)
      return this.genericColumns.every(col => {
        // If undefined (no interaction) -> Show All (Pass)
        if (this.selectedFilterValues[col.key] === undefined || this.selectedFilterValues[col.key] === null) {
          return true;
        }

        // If empty set (User unchecked all) -> Show None (Fail)
        if (this.selectedFilterValues[col.key].size === 0) {
          return false;
        }

        return this.selectedFilterValues[col.key].has(row[col.key]);
      });
    });
    // Recalculate totals based on new visible data? 
    // Or totals only for CHECKED rows? 
    // Usually totals are for visible rows or checked rows. 
    // User asked for "Select rows -> Total". 
    // If I filter, do I keep my row selections?
    // Rows are objects. Set<any> works by reference. So selections are preserved even if hidden.
    // But `isAllSelected` checks `genericTableData.length` (visible).
    // So if I filter to 5 rows and Select All, I select 5 rows. Correct.
    this.selectedRows.clear(); // Clear selection on filter change? safely yes, or specific logic.
    this.calculateTotals();
  }

  ngOnDestroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
    if (this.isLoading) {
      this.disableGlobalRunFreeze();
    }
  }
}

